import WaveSurfer from './wavesurfer.esm.js';
import { CartLinesUpdateEvent } from '@shopify/events';

const root = document.querySelector('#vm-builder');
if (!root) throw new Error('VoiceWave customizer root not found');

const $ = selector => root.querySelector(selector);
const $$ = selector => [...root.querySelectorAll(selector)];
const secureUploadEnabled = root.dataset.secureUpload === 'true';
const isEditorPreview = Boolean(window.Shopify?.designMode);
const model = {
  step: 1,
  file: null,
  mediaUrl: '',
  sourceDuration: 0,
  memoryId: '',
  peaks: [],
  canonicalPeaks: [],
  canonicalMinMax: [],
  duration: 0,
  durationMs: 0,
  trimStart: 0,
  trimEnd: 1,
  appliedTrimStart: 0,
  appliedTrimEnd: 1,
  appliedCanonicalPeaks: [],
  appliedCanonicalMinMax: [],
  appliedTrimmedSequenceHash: '',
  cropApplyGeneration: 0,
  cropSyncPending: false,
  cropSyncError: '',
  cropSyncPromise: Promise.resolve({ ok: true }),
  cropApplied: false,
  isTrimming: false,
  artworkLayout: 'classic',
  selectedVariant: null,
  productionFileUrl: '',
  temporaryProofUrl: '',
  proofUrl: '',
  revisionId: '',
  artworkId: '',
  productionSha256: '',
  productionByteSize: 0,
  productionMetadata: null,
  providerPreviewUrl: '',
  providerRoomUrl: '',
  providerGallery: [],
  providerGalleryActiveIndex: 0,
  providerPreviewStatus: 'idle',
  providerPreviewFingerprint: '',
  approvalTextVersion: 'phase3-proof-confirmation-v1',
  approved: false,
  productionKey: '',
  statusUrl: '',
  cartAttemptQuarantined: false,
  cartViewReady: false
};
let waveSurfer = null;
let recorder = null;
let recordingStream = null;
let chunks = [];
let uploadSession = null;
let mockupGeneration = 0;
let designGeneration = 0;
let proofImageRequestToken = 0;
let activeDesignController = null;

function createProofImageLoadCoordinator() {
  let active = null;
  return {
    run(key, loader) {
      if (active?.key === key) return active.promise;
      const entry = { key, promise: null };
      let value;
      try {
        value = loader();
      } catch (error) {
        value = Promise.reject(error);
      }
      entry.promise = Promise.resolve(value).finally(() => {
        if (active === entry) active = null;
      });
      active = entry;
      return entry.promise;
    },
    clear() {
      active = null;
    }
  };
}

const proofImageLoadCoordinator = createProofImageLoadCoordinator();

function cancelDesignGeneration() {
  designGeneration += 1;
  proofImageLoadCoordinator.clear();
  proofImageRequestToken += 1;
  activeDesignController?.abort();
  activeDesignController = null;
}

function createUploadSession() {
  uploadSession = { id: crypto.randomUUID(), abortController: new AbortController(), requests: new Set(), memoryId: '', uploadId: '', cancelled: false };
  return uploadSession;
}

function assertActiveUpload(session) {
  if (session.cancelled || session !== uploadSession || session.abortController.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
}

const money = amount => {
  const value = Number(amount || 0);
  if (!value) return '—';
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: root.dataset.currency || 'USD' }).format(value); }
  catch (_) { return `$${value.toFixed(2)}`; }
};
const formatTime = seconds => {
  const value = Math.max(0, Number(seconds) || 0);
  if (value < 10) {
    const minutes = Math.floor(value / 60);
    return `${minutes}:${(value - minutes * 60).toFixed(2).padStart(5, '0')}`;
  }
  return `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`;
};
const formatPreciseTime = seconds => {
  const value = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(value / 60);
  return `${minutes}:${(value - minutes * 60).toFixed(2).padStart(5, '0')}`;
};
const readableTextColor = hex => {
  const value = String(hex || '#ffffff').replace('#', '');
  const full = value.length === 3 ? value.split('').map(char => char + char).join('') : value.padEnd(6, 'f').slice(0, 6);
  const [red, green, blue] = [0, 2, 4].map(index => parseInt(full.slice(index, index + 2), 16) || 0);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance < .52 ? '#fff8ec' : '#28221e';
};
const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

function createMockupLifecycle({ now = () => Date.now(), waitUntil, fetchJson, onState, onFingerprint, onReady }) {
  let generation = 0;
  let activeController = null;
  const AUTO_POLL_WINDOW_MS = 180000;
  const SLOW_NOTICE_MS = 45000;
  const clampRetryAfter = value => Math.min(15000, Math.max(2000, Number(value) || 5000));

  const cancel = () => {
    generation += 1;
    activeController?.abort();
    activeController = null;
  };

  const pollTask = async ({ endpoint, fingerprint, ownGeneration, controller, startedAt, retryAfterMs = 5000, slowNotified = false }) => {
    const active = () => ownGeneration === generation && !controller.signal.aborted;
    let nextRetryMs = clampRetryAfter(retryAfterMs);
    let hasSlowNotice = slowNotified;
    while (active()) {
      if (now() - startedAt >= AUTO_POLL_WINDOW_MS) {
        onState('delayed');
        return null;
      }
      const absoluteDeadline = Math.min(startedAt + AUTO_POLL_WINDOW_MS, now() + nextRetryMs);
      await waitUntil(absoluteDeadline, controller.signal);
      if (!active()) return null;
      const response = await fetchJson(`${endpoint}?fingerprint=${encodeURIComponent(fingerprint)}`, { headers: { Accept: 'application/json' }, signal: controller.signal });
      const payload = await response.json().catch(() => ({}));
      if (!active()) return null;
      if (payload.resultUrl) {
        onState('ready');
        onReady({ ...payload, fingerprint });
        return payload;
      }
      if (!response.ok && response.status !== 202) throw new Error(payload.error || 'Product preview is not available.');
      nextRetryMs = clampRetryAfter(payload.retryAfterMs);
      if (now() - startedAt >= SLOW_NOTICE_MS) {
        if (!hasSlowNotice) {
          hasSlowNotice = true;
          onState('delayed');
        }
      } else {
        onState('processing');
      }
    }
    return null;
  };

  const start = async ({ endpoint, body }) => {
    cancel();
    const ownGeneration = generation;
    const controller = new AbortController();
    activeController = controller;
    const startedAt = now();
    const active = () => ownGeneration === generation && !controller.signal.aborted;
    try {
      onState('starting');
      const response = await fetchJson(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
      const payload = await response.json().catch(() => ({}));
      if (!active()) return null;
      if (!response.ok && response.status !== 202) throw new Error(payload.error || 'Product preview could not be started.');
      if (payload.resultUrl) {
        onState('ready');
        onReady(payload);
        return payload;
      }
      const fingerprint = payload.fingerprint || '';
      if (!fingerprint) throw new Error('Product preview identity was not returned.');
      onFingerprint?.(fingerprint);
      onState('processing');
      return await pollTask({ endpoint, fingerprint, ownGeneration, controller, startedAt, retryAfterMs: payload.retryAfterMs });
    } catch (error) {
      if (!active() || error?.name === 'AbortError') return null;
      onState('unavailable', error);
      return null;
    } finally {
      if (ownGeneration === generation) activeController = null;
    }
  };

  const resume = async ({ endpoint, fingerprint }) => {
    if (!fingerprint) return null;
    cancel();
    const ownGeneration = generation;
    const controller = new AbortController();
    activeController = controller;
    const startedAt = now() - SLOW_NOTICE_MS;
    const active = () => ownGeneration === generation && !controller.signal.aborted;
    try {
      onFingerprint?.(fingerprint);
      onState('delayed');
      return await pollTask({ endpoint, fingerprint, ownGeneration, controller, startedAt, slowNotified: true });
    } catch (error) {
      if (!active() || error?.name === 'AbortError') return null;
      onState('unavailable', error);
      return null;
    } finally {
      if (ownGeneration === generation) activeController = null;
    }
  };

  return { start, resume, cancel };
}

const mockupLifecycle = createMockupLifecycle({
  now: () => Date.now(),
  waitUntil: (absoluteDeadline, signal) => new Promise(resolve => {
    const timer = setTimeout(resolve, Math.max(0, absoluteDeadline - Date.now()));
    signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
  }),
  fetchJson: (url, options) => fetch(url, options),
  onState: (status, error) => {
    model.providerPreviewStatus = status;
    if (error) model.providerPreviewError = error.message || 'The personalized product preview is unavailable.';
    else if (status !== 'unavailable') model.providerPreviewError = '';
    setupFinalScenes();
  },
  onFingerprint: fingerprint => {
    model.providerPreviewFingerprint = fingerprint;
  },
  onReady: payload => {
    model.providerPreviewFingerprint = payload.fingerprint || model.providerPreviewFingerprint;
    model.providerPreviewUrl = payload.resultUrl;
    model.providerGalleryActiveIndex = 0;
    setupFinalScenes();
  }
});

async function requestExactProductPreview(selected) {
  model.providerPreviewFingerprint = '';
  await mockupLifecycle.start({
    endpoint: root.dataset.mockupUrl,
    body: { memoryId: model.memoryId, catalogProductId: selected.catalogProductId, catalogVariantId: selected.catalogVariantId }
  });
}

async function resumeExactProductPreview() {
  const fingerprint = model.providerPreviewFingerprint;
  if (!fingerprint) return model.selectedVariant ? requestExactProductPreview(model.selectedVariant) : null;
  return mockupLifecycle.resume({ endpoint: root.dataset.mockupUrl, fingerprint });
}

function setUploadStatus(state, title, detail = '', progress = null) {
  const status = $('[data-upload-status]');
  status.hidden = state === 'idle';
  status.dataset.state = state;
  $('[data-upload-status-title]').textContent = title;
  $('[data-upload-status-detail]').textContent = detail;
  const bar = $('[data-upload-progress]');
  bar.hidden = progress === null;
  if (progress !== null) bar.value = Math.max(0, Math.min(100, progress));
  const cancel = $('[data-cancel-upload]');
  if (cancel) cancel.hidden = !['preparing', 'uploading', 'processing', 'recording'].includes(state);
}

function readMediaDuration(url, file) {
  return new Promise(resolve => {
    const media = document.createElement('audio');
    const finish = value => {
      media.removeAttribute('src');
      media.load();
      resolve(Number.isFinite(value) && value > 0 ? value : 0);
    };
    media.preload = 'metadata';
    media.onloadedmetadata = () => finish(media.duration);
    media.onerror = () => finish(0);
    media.src = url;
  });
}

function uploadToPrivateStorage(url, file, contentType, session) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    session.requests.add(request);
    request.open('PUT', url);
    request.setRequestHeader('Content-Type', contentType);
    request.upload.addEventListener('progress', event => {
      if (!event.lengthComputable) return;
      const percent = Math.round(event.loaded / event.total * 100);
      setUploadStatus('uploading', `Uploading your recording — ${percent}%`, `${(event.loaded / 1048576).toFixed(1)} MB of ${(event.total / 1048576).toFixed(1)} MB`, percent);
    });
    request.addEventListener('loadend', () => session.requests.delete(request));
    request.addEventListener('load', () => request.status >= 200 && request.status < 300 ? resolve() : reject(new Error(`Private storage returned HTTP ${request.status}.`)));
    request.addEventListener('error', () => reject(new Error('The upload was interrupted. Check your connection and try again.')));
    request.addEventListener('abort', () => reject(new Error('The upload was cancelled.')));
    request.send(file);
  });
}

function uploadMultipartToPrivateStorage(parts, file, session) {
  const partSize = Number(parts?.partSize || 0);
  const partUrls = Array.isArray(parts?.partUrls) ? parts.partUrls : [];
  if (!partSize || !partUrls.length) return Promise.reject(new Error('Multipart upload details are invalid.'));
  const uploaded = new Array(partUrls.length).fill(0);
  const putPart = (index, url) => new Promise((resolve, reject) => {
    const start = index * partSize;
    const request = new XMLHttpRequest();
    session.requests.add(request);
    request.open('PUT', url);
    request.upload.addEventListener('progress', event => {
      uploaded[index] = event.lengthComputable ? event.loaded : 0;
      const loaded = uploaded.reduce((sum, value) => sum + value, 0);
      setUploadStatus('uploading', `Uploading your recording - ${Math.round(loaded / file.size * 100)}%`, `${(loaded / 1048576).toFixed(1)} MB of ${(file.size / 1048576).toFixed(1)} MB`, loaded / file.size * 100);
    });
    request.addEventListener('load', () => {
      if (request.status < 200 || request.status >= 300) return reject(new Error(`Private storage returned HTTP ${request.status} for upload part ${index + 1}.`));
      const etag = request.getResponseHeader('ETag') || request.getResponseHeader('etag');
      if (!etag) return reject(new Error(`Private storage did not return an ETag for upload part ${index + 1}.`));
      resolve({ PartNumber: index + 1, ETag: etag });
    });
    request.addEventListener('error', () => reject(new Error('The multipart upload was interrupted. Check your connection and try again.')));
    request.addEventListener('abort', () => reject(new Error('The multipart upload was cancelled.')));
    request.addEventListener('loadend', () => session.requests.delete(request));
    request.send(file.slice(start, Math.min(file.size, start + partSize)));
  });
  const results = [];
  let next = 0;
  const worker = async () => {
    while (next < partUrls.length) {
      const index = next++;
      results[index] = await putPart(index, partUrls[index]);
    }
  };
  return Promise.all(Array.from({ length: Math.min(4, partUrls.length) }, worker)).then(() => results);
}

function designState() {
  const selected = model.selectedVariant || {};
  const durationMs = Math.max(0, Math.round(Number(model.durationMs || model.duration * 1000) || 0));
  const trimStartMs = Math.max(0, Math.min(durationMs, Math.round(durationMs * model.appliedTrimStart)));
  const trimEndMs = Math.max(trimStartMs, Math.min(durationMs, Math.round(durationMs * model.appliedTrimEnd)));
  const textColor = $('[data-auto-text]').checked ? readableTextColor($('[data-bg]').value) : $('[data-text-color]').value;
  return {
    title: $('[data-title]').value.trim(),
    kicker: $('[data-kicker]').value.trim(),
    date: $('[data-date]').value.trim(),
    waveColor: $('[data-color]').value,
    background: $('[data-bg]').value,
    textColor,
    autoTextColor: $('[data-auto-text]').checked,
    fontStyle: $('input[name="vm-font"]:checked')?.value || 'editorial',
    artworkLayout: model.artworkLayout,
    waveformStyle: 'fine-bars',
    trimStart: Number(model.appliedTrimStart.toFixed(4)),
    trimEnd: Number(model.appliedTrimEnd.toFixed(4)),
    durationMs,
    trimStartMs,
    trimEndMs,
    productUid: selected.productUid || '',
    shopifyVariantId: selected.shopifyVariantId || '',
    familyKey: selected.familyKey || '',
    orientation: selected.orientation || '',
    size: selected.size || '',
    attributes: selected.attributes || {}
  };
}

function invalidateProduction() {
  cancelDesignGeneration();
  mockupLifecycle.cancel();
  model.productionFileUrl = '';
  model.temporaryProofUrl = '';
  model.proofUrl = '';
  model.revisionId = '';
  model.artworkId = '';
  model.productionSha256 = '';
  model.productionByteSize = 0;
  model.productionMetadata = null;
  model.providerPreviewUrl = '';
  model.providerRoomUrl = '';
  model.approved = false;
  model.productionKey = '';
  model.cartAttemptQuarantined = false;
  model.cartViewReady = false;
  const proof = $('[data-production-proof]');
  proof.hidden = true;
  proof.removeAttribute('src');
  const previewNote = $('[data-preview-note]');
  if (previewNote) previewNote.hidden = true;
  $('[data-final-tabs]').hidden = true;
  $('[data-review-production]').textContent = 'Generate a new exact proof';
  $('[data-add-cart]').disabled = true;
  $('[data-confirm-details]').checked = false;
  $('[data-confirm-proof]').checked = false;
}

function setStep(step) {
  model.step = step;
  root.dataset.currentStep = String(step);
  $$('[data-step]').forEach(section => {
    const active = Number(section.dataset.step) === step;
    section.hidden = !active;
    section.classList.toggle('is-active', active);
  });
  $$('[data-step-nav]').forEach(button => {
    const number = Number(button.dataset.stepNav);
    const active = number === step;
    button.classList.toggle('is-active', active);
    button.classList.toggle('is-complete', number < step);
    if (active) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
  });
  $('[data-preview-heading]').textContent = step === 4 ? 'Final production preview' : 'Live design preview';
  const priceWrap = $('[data-price-wrap]');
  if (priceWrap) priceWrap.hidden = step < 3 || !Number(model.selectedVariant?.retailPrice || 0);
  applyBoardGeometry(step);
  if (step === 1 && model.peaks.length) showFullWaveform();
  if (step === 2 && model.peaks.length) model.isTrimming ? showTrimEditor() : showArtworkResult();
  if (step === 3 && model.peaks.length) showArtworkResult();
  if (step < 4) showPanel('design');
  requestAnimationFrame(() => {
    const pageScroller = document.querySelector('.page-wrapper');
    if (pageScroller instanceof HTMLElement) {
      const targetTop = pageScroller.scrollTop + root.getBoundingClientRect().top;
      pageScroller.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
      if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } else {
      root.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  });
}

function applyBoardGeometry(step = model.step) {
  const board = $('[data-view-panel="design"]');
  // Keep the editing preview stable. Exact product dimensions are rendered in
  // the separate Exact Print Proof, not by resizing this board on every choice.
  board.style.removeProperty('width');
  board.style.removeProperty('height');
  board.style.aspectRatio = '4 / 3';
  board.dataset.layout = 'design';
  const face = model.selectedVariant?.faceMm || model.selectedVariant?.totalPrintMm;
  $('[data-preview-note]').textContent = step === 1
    ? 'This is your sound-art design. A print-ready proof appears after you choose an exact product.'
    : step < 3 || !face?.width || !face?.height
      ? 'Choose a layout, colors, and words. Your selected product will use this same design.'
      : `Editing preview stays centered. Exact Print Proof uses the selected product dimensions · ${face.width} × ${face.height} mm.`;
  requestAnimationFrame(renderArtworkWaveform);
}

function unlockDesign() {
  $('[data-next="2"]').disabled = false;
  $('[data-step-nav="2"]').disabled = false;
}

function unlockProduct() {
  $('[data-step-nav="3"]').disabled = false;
}

function unlockReview() {
  $('[data-step-nav="4"]').disabled = false;
}

function currentProofImageUrl() {
  return String(model.proofUrl || model.productionFileUrl || model.temporaryProofUrl || '').trim();
}

function reportProofImageError(error) {
  if (error?.name === 'AbortError') return;
  const node = $('[data-order-error]');
  if (node) {
    node.hidden = false;
    node.textContent = error?.message || 'The exact print artwork could not be displayed. Generate the proof again.';
  }
}

async function ensureProductionProofImage(url = currentProofImageUrl(), { generation = designGeneration } = {}) {
  const proof = $('[data-production-proof]');
  const targetUrl = String(url || '').trim();
  if (!proof || !targetUrl) return false;
  const targetGeneration = generation;
  const requestKey = `${targetGeneration}\n${targetUrl}`;

  return proofImageLoadCoordinator.run(requestKey, async () => {
    const requestToken = ++proofImageRequestToken;
    const isCurrent = () => requestToken === proofImageRequestToken
      && targetGeneration === designGeneration
      && currentProofImageUrl() === targetUrl;
    const abortIfStale = () => {
      if (!isCurrent()) throw new DOMException('Proof image load superseded', 'AbortError');
    };
    const hasNaturalImage = () => proof.complete && proof.naturalWidth > 0 && proof.naturalHeight > 0;

    const loadOnce = forceReload => new Promise((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        window.clearTimeout(timeout);
        proof.removeEventListener('load', onLoad);
        proof.removeEventListener('error', onError);
      };
      const settle = (callback, value) => {
        if (settled) return;
        settled = true;
        cleanup();
        callback(value);
      };
      const staleError = () => new DOMException('Proof image load superseded', 'AbortError');
      const onLoad = () => {
        if (!isCurrent()) return settle(reject, staleError());
        if (!hasNaturalImage()) return settle(reject, new Error('The exact print artwork loaded without image dimensions.'));
        settle(resolve, true);
      };
      const onError = () => settle(reject, isCurrent() ? new Error('The exact print artwork could not be loaded.') : staleError());
      const timeout = window.setTimeout(() => settle(reject, isCurrent() ? new Error('The exact print artwork did not finish loading.') : staleError()), 10000);
      proof.addEventListener('load', onLoad);
      proof.addEventListener('error', onError);
      const currentSrc = proof.getAttribute('src') || '';
      if (forceReload) proof.removeAttribute('src');
      if (forceReload || currentSrc !== targetUrl) proof.src = targetUrl;
      proof.hidden = false;
      if (hasNaturalImage()) onLoad();
      else if (proof.complete && proof.getAttribute('src') === targetUrl && proof.naturalWidth === 0) onError();
    });

    const sameUrlAlreadyFailed = proof.getAttribute('src') === targetUrl && proof.complete && !hasNaturalImage();
    try {
      await loadOnce(sameUrlAlreadyFailed);
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      abortIfStale();
      if (sameUrlAlreadyFailed) throw error;
      await loadOnce(true);
    }
    abortIfStale();

    if (proof.decode) {
      try {
        await proof.decode();
      } catch (error) {
        abortIfStale();
        if (!(proof.naturalWidth > 0 && proof.naturalHeight > 0)) throw error;
      }
    }
    abortIfStale();
    if (!(proof.naturalWidth > 0 && proof.naturalHeight > 0)) throw new Error('The exact print artwork is unavailable. Generate the proof again.');
    return true;
  });
}

function showPanel(view) {
  $$('[data-view-panel]').forEach(panel => { panel.hidden = panel.dataset.viewPanel !== view; });
  $$('[data-view]').forEach(button => {
    const active = button.dataset.view === view;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-selected', String(active));
  });
  if (view === 'proof' && currentProofImageUrl()) {
    ensureProductionProofImage().catch(reportProofImageError);
  }
}

function syncDesignPreview() {
  const state = designState();
  const board = $('[data-view-panel="design"]');
  board.style.background = state.background;
  board.style.setProperty('--vm-artwork-background', state.background);
  board.style.color = state.textColor;
  board.style.setProperty('--vm-wave-color', state.waveColor);
  board.dataset.font = state.fontStyle;
  board.dataset.artLayout = state.artworkLayout;
  $('[data-preview-title]').textContent = state.title;
  $('[data-preview-kicker]').textContent = state.kicker;
  $('[data-preview-date]').textContent = state.date;
  if (waveSurfer) waveSurfer.setOptions({ waveColor: state.waveColor, progressColor: '#0d4b48' });
  renderArtworkWaveform();
  renderLayoutPreviews();
  invalidateProduction();
}

function normalizedPeaks(payload) {
  const source = payload?.canonicalPeaks || payload?.media?.canonicalPeaks || payload?.peaks || payload?.waveform?.peaks || payload?.waveform;
  const channel = Array.isArray(source?.[0]) ? source[0] : source;
  if (!Array.isArray(channel) || !channel.length) return [];
  const numeric = channel.map(value => Number(value) || 0);
  const maxAmplitude = numeric.reduce((maximum, value) => Math.max(maximum, Math.abs(value)), 0);
  if (!maxAmplitude) return numeric;
  const scale = maxAmplitude > 1 ? maxAmplitude : 1;
  return numeric.map(value => Math.min(1, Math.max(-1, value / scale)));
}

function normalizedDurationMs(payload) {
  const value = Number(payload?.durationMs ?? payload?.media?.durationMs);
  return Number.isInteger(value) && value > 0 ? value : 0;
}

function canonicalSnapshot(payload, fallbackPeaks, durationMs) {
  const canonicalMinMax = payload?.canonicalMinMax || payload?.canonicalMedia?.canonicalMinMax || payload?.media?.canonicalMinMax || [];
  return {
    memoryId: model.memoryId,
    canonicalMinMax,
    canonicalPeaks: fallbackPeaks,
    durationMs,
    sequenceHash: payload?.sourceSequenceHash || payload?.canonicalMedia?.sourceSequenceHash || payload?.media?.sourceSequenceHash || ''
  };
}

function statusUrlFor(memoryId) {
  const base = model.statusUrl || root.dataset.statusUrl || root.dataset.peaksUrl;
  if (base.includes('memoryId=')) return base;
  return `${base}${base.includes('?') ? '&' : '?'}memoryId=${encodeURIComponent(memoryId)}`;
}

async function getCanonicalPeaks(completePayload, session) {
  const inline = normalizedPeaks(completePayload);
  const inlineDurationMs = normalizedDurationMs(completePayload);
  if (inline.length && inlineDurationMs > 0) return { peaks: inline, durationMs: inlineDurationMs, snapshot: canonicalSnapshot(completePayload, inline, inlineDurationMs) };
  let lastError = 'The canonical waveform is still processing.';
  for (let attempt = 0; attempt < 30; attempt += 1) {
    assertActiveUpload(session);
    const response = await fetch(statusUrlFor(session.memoryId), { headers: { Accept: 'application/json' }, cache: 'no-store', signal: session.abortController.signal });
    const payload = await response.json().catch(() => ({}));
    const peaks = normalizedPeaks(payload);
    const durationMs = normalizedDurationMs(payload);
    if (response.ok && payload.status === 'ready' && peaks.length && durationMs > 0) {
      return { peaks, durationMs, snapshot: canonicalSnapshot(payload, peaks, durationMs) };
    }
    if (payload.status === 'processing_failed' || (response.status >= 400 && ![404, 409, 425].includes(response.status))) {
      const code = payload.processing?.errorCode || payload.errorCode;
      lastError = payload.error || payload.processing?.errorMessage || (code ? `Media processing failed (${code}).` : lastError);
      break;
    }
    lastError = payload.error || payload.processing?.errorMessage || lastError;
    setUploadStatus('processing', 'Creating your real soundwave…', `Media processing: ${payload.processing?.stage || payload.status || 'queued'}.`);
    await wait(attempt < 20 ? 500 : Math.min(1000 + (attempt - 20) * 400, 5000));
  }
  throw new Error(lastError);
}

async function mountWaveform(peaks, durationMs) {
  if (!Number.isInteger(durationMs) || durationMs <= 0) throw new Error('The recording duration could not be read. Please try another file.');
  model.peaks = peaks;
  model.canonicalPeaks = peaks;
  model.durationMs = durationMs;
  model.duration = durationMs / 1000;
  const duration = model.duration;
  model.appliedTrimStart = 0;
  model.appliedTrimEnd = 1;
  model.appliedCanonicalPeaks = [];
  model.appliedCanonicalMinMax = [];
  model.appliedTrimmedSequenceHash = '';
  model.cropApplied = false;
  model.isTrimming = false;
  $('[data-wave-empty]').hidden = true;
  if (waveSurfer) waveSurfer.destroy();
  waveSurfer = WaveSurfer.create({
    container: $('[data-wavesurfer]'),
    height: window.innerWidth < 760 ? 118 : 176,
    waveColor: $('[data-color]').value,
    progressColor: '#0d4b48',
    cursorColor: '#a8664d',
    cursorWidth: 1,
    barWidth: 1,
    barGap: 1,
    barRadius: 0,
    barMinHeight: 1,
    normalize: false,
    interact: true,
    dragToSeek: true
  });
  await waveSurfer.load(model.mediaUrl, [peaks], model.duration);
  $('[data-player]').hidden = false;
  $('[data-trim-overlay]').hidden = true;
  $('[data-duration]').textContent = formatTime(duration);
  waveSurfer.on('timeupdate', current => {
    $('[data-current-time]').textContent = formatTime(current);
    $('[data-seek]').value = String(duration ? current / duration : 0);
    const end = model.isTrimming ? model.trimEnd : model.appliedTrimEnd;
    if (waveSurfer.isPlaying() && current >= duration * end) waveSurfer.pause();
  });
  waveSurfer.on('play', () => { $('[data-play]').textContent = 'Ⅱ'; });
  waveSurfer.on('pause', () => { $('[data-play]').textContent = '▶'; });
  renderLayoutPreviews();
}

function isCustomerAudioFile(file) {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  return type.startsWith('audio/') || /\.(mp3|wav|m4a|aac)$/i.test(name);
}

async function uploadFile(file) {
  if (!file) return;
  if (!isCustomerAudioFile(file)) {
    const errorNode = $('[data-upload-error]');
    errorNode.hidden = false;
    errorNode.textContent = 'Video upload is not supported yet. Choose an audio file or record directly in your browser.';
    const fileInput = $('#vm-media-file');
    if (fileInput) fileInput.value = '';
    $('[data-file-name]').hidden = true;
    return;
  }
  if (uploadSession) await cancelCurrentUpload();
  const session = createUploadSession();
  root.voiceWaveCanonicalSnapshot = null;
  root.dispatchEvent(new CustomEvent('vm:canonical-reset', { detail: { memoryId: '' } }));
  const errorNode = $('[data-upload-error]');
  errorNode.hidden = true;
  errorNode.textContent = '';
  $('[data-next="2"]').disabled = true;
  model.file = file;
  model.memoryId = '';
  model.statusUrl = '';
  model.peaks = [];
  model.canonicalPeaks = [];
  model.canonicalMinMax = [];
  model.cropApplyGeneration += 1;
  model.cropSyncPending = false;
  model.cropSyncError = '';
  model.cropSyncPromise = Promise.resolve({ ok: true });
  root.voiceWaveCropSnapshot = null;
  root.dispatchEvent(new CustomEvent('vm:crop-reset'));
  model.durationMs = 0;
  model.trimStart = 0;
  model.trimEnd = 1;
  model.appliedTrimStart = 0;
  model.appliedTrimEnd = 1;
  model.appliedCanonicalPeaks = [];
  model.appliedCanonicalMinMax = [];
  model.appliedTrimmedSequenceHash = '';
  invalidateProduction();
  if (model.mediaUrl) URL.revokeObjectURL(model.mediaUrl);
  model.mediaUrl = URL.createObjectURL(file);
  model.sourceDuration = await readMediaDuration(model.mediaUrl, file);
  $('[data-file-name]').textContent = `${file.name} · processing`;
  $('[data-wave-empty]').hidden = false;
  $('[data-wavesurfer]').hidden = true;
  $('[data-art-waveform]').hidden = true;

  if (!secureUploadEnabled) {
    setUploadStatus('idle', '');
    errorNode.hidden = false;
    errorNode.textContent = 'Production upload is disabled in this theme. Enable it only after the upload and canonical peaks services are deployed.';
    return;
  }

  try {
    setUploadStatus('preparing', 'Preparing your recording…', 'Checking the file and creating a private upload.');
    const initResponse = await fetch(root.dataset.uploadUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operation: 'init', filename: file.name, contentType: file.type || 'application/octet-stream', byteSize: file.size }), signal: session.abortController.signal
    });
    const initPayload = await initResponse.json().catch(() => ({}));
    if (!initResponse.ok || !initPayload.memoryId || !initPayload.uploadUrl) throw new Error(initPayload.error || 'Private upload could not start.');
    model.memoryId = initPayload.memoryId;
    session.memoryId = initPayload.memoryId;
    session.uploadId = initPayload.multipart?.uploadId || '';
    model.statusUrl = initPayload.statusUrl || root.dataset.statusUrl;

    setUploadStatus('uploading', 'Uploading your recording — 0%', 'Your audio file is being uploaded privately.', 0);
    let multipartParts = null;
    if (initPayload.uploadMode === 'multipart' && initPayload.multipart?.uploadId) {
      multipartParts = await uploadMultipartToPrivateStorage(initPayload.multipart, file, session);
      const multipartCompleteResponse = await fetch(initPayload.completeUrl || root.dataset.uploadUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'multipart_complete', memoryId: session.memoryId, uploadId: initPayload.multipart.uploadId, parts: multipartParts }), signal: session.abortController.signal
      });
      const multipartPayload = await multipartCompleteResponse.json().catch(() => ({}));
      if (!multipartCompleteResponse.ok) throw new Error(multipartPayload.error || 'Multipart upload could not be completed.');
    } else {
      await uploadToPrivateStorage(initPayload.uploadUrl, file, String(file.type || 'application/octet-stream').split(';', 1)[0], session);
    }

    setUploadStatus('processing', 'Creating your real soundwave…', 'Upload complete. We are building the real waveform from your recording.');
    const completeResponse = await fetch(root.dataset.uploadUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operation: 'complete', memoryId: session.memoryId }), signal: session.abortController.signal
    });
    const completePayload = await completeResponse.json().catch(() => ({}));
    if (!completeResponse.ok) throw new Error(completePayload.error || 'Upload processing could not be completed.');

    const canonical = await getCanonicalPeaks(completePayload, session);
    model.canonicalMinMax = Array.isArray(canonical.snapshot?.canonicalMinMax) ? canonical.snapshot.canonicalMinMax : [];
    assertActiveUpload(session);
    await mountWaveform(canonical.peaks, canonical.durationMs);
    root.voiceWaveCanonicalSnapshot = canonical.snapshot;
    root.dispatchEvent(new CustomEvent('vm:canonical-ready', { detail: canonical.snapshot }));
    showFullWaveform();
    $('[data-file-name]').textContent = `${file.name} · waveform ready`;
    setUploadStatus('ready', 'Your real soundwave is ready', 'You can keep the full recording or trim a meaningful section.');
    $('[data-preview-note]').textContent = 'Created from your recording. Keep the full sound or shape a meaningful section next.';
    unlockDesign();
  } catch (error) {
    if (session.cancelled || error?.name === 'AbortError') return;
    setUploadStatus('error', 'We could not create the soundwave', 'Choose the file again or try another recording.');
    errorNode.hidden = false;
    errorNode.textContent = error.message || 'The recording could not be processed.';
  }
}

async function cancelCurrentUpload() {
  const session = uploadSession;
  if (!session) return;
  session.cancelled = true;
  session.abortController.abort();
  session.requests.forEach(request => request.abort());
  if (recorder?.state === 'recording') {
    recorder.ondataavailable = null;
    recorder.onstop = null;
    recorder.stop();
  }
  recordingStream?.getTracks().forEach(track => track.stop());
  recordingStream = null;
  chunks = [];
  const memoryId = session.memoryId || model.memoryId;
  uploadSession = null;
  model.file = null;
  model.memoryId = '';
  model.statusUrl = '';
  model.peaks = [];
  model.canonicalPeaks = [];
  model.canonicalMinMax = [];
  model.cropApplyGeneration += 1;
  model.cropSyncPending = false;
  model.cropSyncError = '';
  model.cropSyncPromise = Promise.resolve({ ok: true });
  invalidateProduction();
  root.voiceWaveCanonicalSnapshot = null;
  root.voiceWaveCropSnapshot = null;
  root.dispatchEvent(new CustomEvent('vm:canonical-reset', { detail: { memoryId: '' } }));
  root.dispatchEvent(new CustomEvent('vm:crop-reset'));
  $('[data-art-waveform]').hidden = true;
  $('[data-wavesurfer]').hidden = true;
  $('[data-wave-empty]').hidden = false;
  $('[data-file-name]').textContent = 'No file selected';
  $('#vm-media-file').value = '';
  setUploadStatus('idle', '');
  if (memoryId) fetch(root.dataset.uploadUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ operation: 'cancel', memoryId, uploadId: session.uploadId }), keepalive: true }).catch(() => undefined);
}

function syncCrop(changed) {
  const startInput = $('[data-crop-start]');
  const endInput = $('[data-crop-end]');
  let start = Number(startInput.value);
  let end = Number(endInput.value);
  const minimum = model.duration ? Math.min(.02, 1 / model.duration) : .01;
  if (end - start < minimum) {
    if (changed === startInput) start = Math.max(0, end - minimum); else end = Math.min(1, start + minimum);
  }
  startInput.value = String(start);
  endInput.value = String(end);
  model.trimStart = start;
  model.trimEnd = end;
  $('[data-crop-start-label]').textContent = formatPreciseTime(model.duration * start);
  $('[data-crop-end-label]').textContent = model.duration ? formatPreciseTime(model.duration * end) : 'End';
  $('[data-trim-start-readout]').textContent = formatPreciseTime(model.duration * start);
  $('[data-trim-end-readout]').textContent = formatPreciseTime(model.duration * end);
  $('[data-trim-duration]').textContent = formatPreciseTime(model.duration * (end - start));
  root.style.setProperty('--vm-trim-start', `${start * 100}%`);
  root.style.setProperty('--vm-trim-end', `${end * 100}%`);
  invalidateProduction();
}

function selectedPeaks() {
  if (!model.peaks.length) return [];
  if (model.cropApplied && model.appliedCanonicalPeaks.length) return model.appliedCanonicalPeaks;
  const start = Math.max(0, Math.floor(model.appliedTrimStart * model.peaks.length));
  const end = Math.min(model.peaks.length, Math.max(start + 2, Math.ceil(model.appliedTrimEnd * model.peaks.length)));
  return model.peaks.slice(start, end);
}

function resampleAmplitudes(peaks, count) {
  if (!peaks.length || count < 1) return [];
  const absolute = peaks.map(value => Math.abs(Number(value) || 0));
  if (absolute.length <= count) {
    return Array.from({ length: count }, (_, index) => {
      const point = index * (absolute.length - 1) / Math.max(1, count - 1);
      const left = Math.floor(point);
      const right = Math.min(absolute.length - 1, left + 1);
      const mix = point - left;
      return absolute[left] * (1 - mix) + absolute[right] * mix;
    });
  }
  return Array.from({ length: count }, (_, index) => {
    const from = Math.floor(index * absolute.length / count);
    const to = Math.max(from + 1, Math.ceil((index + 1) * absolute.length / count));
    let maximum = 0;
    for (let item = from; item < to && item < absolute.length; item += 1) maximum = Math.max(maximum, absolute[item]);
    return maximum;
  });
}

function drawFineWaveform(canvas, peaks, layout, color) {
  if (!canvas || !peaks.length) return;
  const bounds = canvas.getBoundingClientRect();
  const width = Math.max(90, Math.round(bounds.width || canvas.parentElement?.clientWidth || 500));
  const height = Math.max(54, Math.round(bounds.height || 180));
  const ratio = Math.min(2.5, window.devicePixelRatio || 1);
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  const context = canvas.getContext('2d');
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);
  context.strokeStyle = color;
  context.lineWidth = Math.max(.65, Math.min(.85, width / 720));
  context.lineCap = 'butt';

  const target = layout === 'classic'
    ? Math.max(96, Math.min(420, Math.floor(width / 2.2)))
    : Math.max(120, Math.min(420, Math.floor((width + height) / 2.4)));
  const amplitudes = resampleAmplitudes(peaks, target);
  const maximum = amplitudes.reduce((value, item) => Math.max(value, item), 0) || 1;
  const normalized = amplitudes.map(value => value / maximum);
  context.beginPath();

  if (layout === 'heart') {
    const scaleX = width * .023;
    const scaleY = height * .026;
    normalized.forEach((amplitude, index) => {
      const t = Math.PI * 2 * index / normalized.length;
      const x0 = width / 2 + 16 * Math.sin(t) ** 3 * scaleX;
      const y0 = height / 2 - (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) * scaleY;
      const next = t + .002;
      const x1 = width / 2 + 16 * Math.sin(next) ** 3 * scaleX;
      const y1 = height / 2 - (13 * Math.cos(next) - 5 * Math.cos(2 * next) - 2 * Math.cos(3 * next) - Math.cos(4 * next)) * scaleY;
      const length = Math.hypot(x1 - x0, y1 - y0) || 1;
      const nx = -(y1 - y0) / length;
      const ny = (x1 - x0) / length;
      const bar = .7 + amplitude * Math.min(width, height) * .055;
      context.moveTo(x0 - nx * bar, y0 - ny * bar);
      context.lineTo(x0 + nx * bar, y0 + ny * bar);
    });
  } else if (layout === 'circle') {
    const radius = Math.min(width, height) * .31;
    normalized.forEach((amplitude, index) => {
      const angle = -Math.PI / 2 + Math.PI * 2 * index / normalized.length;
      const bar = .7 + amplitude * Math.min(width, height) * .10;
      const inner = radius - bar;
      const outer = radius + bar;
      context.moveTo(width / 2 + Math.cos(angle) * inner, height / 2 + Math.sin(angle) * inner);
      context.lineTo(width / 2 + Math.cos(angle) * outer, height / 2 + Math.sin(angle) * outer);
    });
  } else {
    const center = height / 2;
    const usableWidth = width * .92;
    const startX = (width - usableWidth) / 2;
    normalized.forEach((amplitude, index) => {
      const x = startX + index * usableWidth / Math.max(1, normalized.length - 1);
      const bar = amplitude < .002 ? .12 : .28 + amplitude * height * .40;
      context.moveTo(x, center - bar);
      context.lineTo(x, center + bar);
    });
  }
  context.stroke();
}

function renderLayoutPreviews() {
  const peaks = selectedPeaks();
  if (!peaks.length) return;
  const startColor = $('[data-v4-stop="0"]')?.value || $('[data-color]')?.value || '#c7441f';
  const endColor = $('[data-v4-stop="1"]')?.value || startColor;
  $$('[data-layout-preview]').forEach(canvas => {
    const ctx = canvas.getContext('2d');
    let strokeStyle = startColor;
    if (ctx && startColor && endColor && startColor !== endColor) {
      const grad = ctx.createLinearGradient(0, 0, canvas.width, 0);
      grad.addColorStop(0, startColor);
      grad.addColorStop(1, endColor);
      strokeStyle = grad;
    }
    drawFineWaveform(canvas, peaks, canvas.dataset.layoutPreview, strokeStyle);
  });
}

function renderArtworkWaveform() {
  // The shared v4 renderer is the single owner of the customer-facing artwork
  // canvas. The legacy controller still owns upload, playback, trimming,
  // product selection, and checkout state, but must never repaint the final
  // artwork with its lower-density display waveform.
  if (window.VoiceWaveRenderCoreV4) {
    root.dispatchEvent(new CustomEvent('vm:v4-render-request'));
    return;
  }
  $('[data-art-waveform]').hidden = true;
  $('[data-preview-status]').textContent = 'Preview renderer is loading';
}

function renderTrimWaveform() {
  if (window.VoiceWaveRenderCoreV4) {
    root.dispatchEvent(new CustomEvent('vm:v4-render-request'));
    return;
  }
  $('[data-art-waveform]').hidden = true;
}

function showFullWaveform() {
  model.isTrimming = false;
  delete root.dataset.trimEditing;
  $('[data-wavesurfer]').hidden = true;
  if (!$('[data-view-panel="design"]')?.dataset.v4Rendered) $('[data-art-waveform]').hidden = true;
  $('[data-trim-overlay]').hidden = true;
  requestAnimationFrame(renderArtworkWaveform);
}

function showTrimEditor() {
  model.isTrimming = true;
  root.dataset.trimEditing = 'true';
  model.trimStart = model.appliedTrimStart;
  model.trimEnd = model.appliedTrimEnd;
  $('[data-crop-start]').value = String(model.trimStart);
  $('[data-crop-end]').value = String(model.trimEnd);
  $('[data-wavesurfer]').hidden = true;
  $('[data-art-waveform]').hidden = false;
  $('[data-trim-overlay]').hidden = false;
  $('[data-crop-editor]').hidden = false;
  $('[data-sound-result]').hidden = true;
  $('[data-design-controls]').hidden = true;
  syncCrop();
  requestAnimationFrame(renderTrimWaveform);
}

function showArtworkResult() {
  model.isTrimming = false;
  delete root.dataset.trimEditing;
  $('[data-wavesurfer]').hidden = true;
  $('[data-art-waveform]').hidden = false;
  $('[data-trim-overlay]').hidden = true;
  $('[data-crop-editor]').hidden = true;
  const soundResult = $('[data-sound-result]');
  if (soundResult) soundResult.hidden = false;
  $('[data-design-controls]').hidden = false;
  const start = model.duration * model.appliedTrimStart;
  const end = model.duration * model.appliedTrimEnd;
  const resultTitle = $('[data-sound-result-title]');
  if (resultTitle) resultTitle.textContent = model.cropApplied ? 'Cropped section' : 'Full recording';
  const resultDetail = $('[data-sound-result-detail]');
  if (resultDetail) resultDetail.textContent = model.cropApplied ? `${formatPreciseTime(start)}–${formatPreciseTime(end)} · ${formatPreciseTime(end - start)} kept and expanded across the artwork.` : `Full recording · ${formatPreciseTime(model.duration)}`;
  const editTrim = $('[data-edit-trim]');
  if (editTrim) editTrim.textContent = model.cropApplied ? 'Edit crop' : 'Trim a section';
  const useFull = $('[data-use-full]');
  if (useFull) useFull.hidden = !model.cropApplied;
  requestAnimationFrame(renderArtworkWaveform);
}

function localCropSnapshot(startMs, endMs) {
  const durationMs = Math.max(1, Number(model.durationMs || 1));
  const startRatio = Math.max(0, Math.min(1, Number(startMs) / durationMs));
  const endRatio = Math.max(startRatio + .001, Math.min(1, Number(endMs) / durationMs));
  const source = model.canonicalPeaks.length ? model.canonicalPeaks : model.peaks;
  const sourceMinMax = model.canonicalMinMax.length ? model.canonicalMinMax : [];
  const from = Math.max(0, Math.min(Math.max(0, source.length - 1), Math.floor(startRatio * source.length)));
  const to = Math.max(from + 1, Math.min(source.length, Math.ceil(endRatio * source.length)));
  const minMaxFrom = Math.max(0, Math.min(Math.max(0, sourceMinMax.length - 1), Math.floor(startRatio * sourceMinMax.length)));
  const minMaxTo = Math.max(minMaxFrom + 1, Math.min(sourceMinMax.length, Math.ceil(endRatio * sourceMinMax.length)));
  return {
    operation: 'trim',
    memoryId: model.memoryId,
    status: 'preview',
    source: 'client-preview',
    durationMs: model.durationMs,
    trimStartMs: startMs,
    trimEndMs: endMs,
    keptDurationMs: endMs - startMs,
    canonicalPeaks: source.slice(from, to),
    canonicalMinMax: sourceMinMax.slice(minMaxFrom, minMaxTo),
    trimmedSequenceHash: ''
  };
}

function commitCrop(payload) {
  const durationMs = Math.max(1, Number(model.durationMs || payload?.durationMs || 1));
  const startMs = Math.max(0, Math.min(durationMs, Math.round(Number(payload?.trimStartMs ?? model.trimStart * durationMs))));
  const endMs = Math.max(startMs + 1, Math.min(durationMs, Math.round(Number(payload?.trimEndMs ?? model.trimEnd * durationMs))));
  model.appliedTrimStart = startMs / durationMs;
  model.appliedTrimEnd = endMs / durationMs;
  model.trimStart = model.appliedTrimStart;
  model.trimEnd = model.appliedTrimEnd;
  model.cropApplied = model.appliedTrimStart > .0001 || model.appliedTrimEnd < .9999;
  model.appliedCanonicalPeaks = Array.isArray(payload?.canonicalPeaks) ? payload.canonicalPeaks.map(Number).filter(Number.isFinite) : [];
  model.appliedCanonicalMinMax = Array.isArray(payload?.canonicalMinMax) ? payload.canonicalMinMax : [];
  model.appliedTrimmedSequenceHash = String(payload?.trimmedSequenceHash || '');
  root.voiceWaveCropSnapshot = { ...payload, trimStartMs: startMs, trimEndMs: endMs, durationMs };
  root.dispatchEvent(new CustomEvent('vm:crop-applied', { detail: root.voiceWaveCropSnapshot }));
  $('[data-crop-start]').value = String(model.appliedTrimStart);
  $('[data-crop-end]').value = String(model.appliedTrimEnd);
  syncCrop();
}

async function applyCropLegacy() {
  const button = $('[data-apply-trim]');
  const error = $('[data-crop-error]');
  const durationMs = Math.max(1, Number(model.durationMs || model.duration * 1000 || 1));
  const startMs = Math.round(durationMs * model.trimStart);
  const endMs = Math.round(durationMs * model.trimEnd);
  if (error) { error.hidden = true; error.textContent = ''; }
  if (button) { button.disabled = true; button.dataset.originalLabel = button.textContent; button.textContent = 'Applying crop…'; }
  try {
    let payload = null;
    const trimUrl = root.dataset.trimUrl || '';
    // Shopify Theme Editor uses a deterministic local fixture and has no
    // customer MemoryDesign row. Customer mode always asks the backend for
    // the canonical crop so the final proof cannot drift from the preview.
    if (!isEditorPreview && trimUrl && model.memoryId) {
      const response = await fetch(trimUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ memoryId: model.memoryId, trimStartMs: startMs, trimEndMs: endMs })
      });
      payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status !== 'ready' || !Array.isArray(payload.canonicalMinMax) || payload.canonicalMinMax.length < 2) {
        throw new Error(payload.error || `The server could not apply this crop (HTTP ${response.status}).`);
      }
    } else {
      payload = localCropSnapshot(startMs, endMs);
    }
    commitCrop(payload);
    showArtworkResult();
    syncDesignPreview();
  } catch (cropError) {
    if (error) { error.hidden = false; error.textContent = cropError instanceof Error ? cropError.message : 'The crop could not be applied. Please try again.'; }
  } finally {
    if (button) { button.disabled = false; button.textContent = button.dataset.originalLabel || 'Apply crop'; delete button.dataset.originalLabel; }
  }
}

async function applyCrop() {
  const button = $('[data-apply-trim]');
  const error = $('[data-crop-error]');
  const durationMs = Math.max(1, Number(model.durationMs || model.duration * 1000 || 1));
  const startMs = Math.round(durationMs * model.trimStart);
  const endMs = Math.round(durationMs * model.trimEnd);
  const requestId = model.cropApplyGeneration + 1;
  model.cropApplyGeneration = requestId;
  const trimUrl = root.dataset.trimUrl || '';
  const shouldSyncCanonical = Boolean(trimUrl && model.memoryId && !Boolean(window.Shopify?.designMode));
  if (error) { error.hidden = true; error.textContent = ''; }
  model.cropSyncPending = shouldSyncCanonical;
  model.cropSyncError = '';
  root.dataset.cropSync = shouldSyncCanonical ? 'pending' : 'ready';

  // Render the selected section locally first. The exact same integer
  // millisecond bounds are sent to the backend in parallel for canonical data.
  commitCrop(localCropSnapshot(startMs, endMs));
  showArtworkResult();
  syncDesignPreview();
  requestAnimationFrame(() => { renderArtworkWaveform(); requestAnimationFrame(renderArtworkWaveform); });
  if (button) { button.disabled = false; button.textContent = button.dataset.originalLabel || 'Apply crop'; delete button.dataset.originalLabel; }

  if (!shouldSyncCanonical) {
    model.cropSyncPromise = Promise.resolve({ ok: true });
    return;
  }

  const syncPromise = (async () => {
    try {
      const response = await fetch(trimUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ memoryId: model.memoryId, trimStartMs: startMs, trimEndMs: endMs })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status !== 'ready' || !Array.isArray(payload.canonicalMinMax) || payload.canonicalMinMax.length < 2) {
        throw new Error(payload.error || `The server could not apply this crop (HTTP ${response.status}).`);
      }
      if (requestId !== model.cropApplyGeneration) return { ok: false, stale: true, error: 'A newer crop selection is already active.' };
      commitCrop({ ...payload, source: 'backend-canonical' });
      showArtworkResult();
      syncDesignPreview();
      requestAnimationFrame(() => { renderArtworkWaveform(); requestAnimationFrame(renderArtworkWaveform); });
      return { ok: true };
    } catch (cropError) {
      const message = cropError instanceof Error ? cropError.message : 'The crop could not be synchronized. Please try again.';
      if (requestId === model.cropApplyGeneration) {
        model.cropSyncError = message;
        root.dataset.cropSync = 'error';
        if (error) { error.hidden = false; error.textContent = `Preview applied. ${message}`; }
      }
      return { ok: false, error: message };
    } finally {
      if (requestId === model.cropApplyGeneration) {
        model.cropSyncPending = false;
        if (root.dataset.cropSync === 'pending') root.dataset.cropSync = 'ready';
      }
    }
  })();
  model.cropSyncPromise = syncPromise;
}

function useFullRecording() {
  model.cropApplyGeneration += 1;
  model.cropSyncPending = false;
  model.cropSyncError = '';
  model.cropSyncPromise = Promise.resolve({ ok: true });
  root.dataset.cropSync = 'ready';
  model.trimStart = 0;
  model.trimEnd = 1;
  model.appliedTrimStart = 0;
  model.appliedTrimEnd = 1;
  model.appliedCanonicalPeaks = [];
  model.appliedCanonicalMinMax = [];
  model.appliedTrimmedSequenceHash = '';
  model.cropApplied = false;
  root.voiceWaveCropSnapshot = null;
  root.dispatchEvent(new CustomEvent('vm:crop-reset'));
  $('[data-crop-start]').value = '0';
  $('[data-crop-end]').value = '1';
  syncCrop();
  showArtworkResult();
}

function setupTrimEditor() {
  const overlay = $('[data-trim-overlay]');
  const startInput = $('[data-crop-start]');
  const endInput = $('[data-crop-end]');
  let dragMode = '';
  let originX = 0;
  let originStart = 0;
  let originEnd = 1;
  const position = event => {
    const bounds = overlay.getBoundingClientRect();
    return Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
  };
  const begin = (mode, event) => {
    dragMode = mode;
    originX = position(event);
    originStart = Number(startInput.value);
    originEnd = Number(endInput.value);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };
  $('[data-trim-handle="start"]').addEventListener('pointerdown', event => begin('start', event));
  $('[data-trim-handle="end"]').addEventListener('pointerdown', event => begin('end', event));
  $('[data-trim-selection]').addEventListener('pointerdown', event => begin('selection', event));
  overlay.addEventListener('pointermove', event => {
    if (!dragMode) return;
    const point = position(event);
    if (dragMode === 'start') startInput.value = String(Math.min(point, Number(endInput.value) - .001));
    if (dragMode === 'end') endInput.value = String(Math.max(point, Number(startInput.value) + .001));
    if (dragMode === 'selection') {
      const width = originEnd - originStart;
      const nextStart = Math.min(1 - width, Math.max(0, originStart + point - originX));
      startInput.value = String(nextStart);
      endInput.value = String(nextStart + width);
    }
    syncCrop(dragMode === 'end' ? endInput : startInput);
  });
  const finish = () => { dragMode = ''; };
  overlay.addEventListener('pointerup', finish);
  overlay.addEventListener('pointercancel', finish);
  $('[data-reset-trim]').addEventListener('click', () => {
    startInput.value = '0'; endInput.value = '1'; syncCrop();
  });
  $('[data-play-selection]').addEventListener('click', () => {
    if (!waveSurfer) return;
    waveSurfer.setTime(model.duration * model.trimStart);
    waveSurfer.play();
  });
}

async function generateProof() {
  const button = $('[data-generate-proof]');
  const selected = model.selectedVariant;
  if (!selected?.productUid || !selected?.shopifyVariantId) return;
  cancelDesignGeneration();
  const ownDesignGeneration = designGeneration;
  const designController = new AbortController();
  activeDesignController = designController;
  const selectedIdentity = `${selected.productUid}:${selected.catalogProductId}:${selected.catalogVariantId}:${selected.shopifyVariantId}`;
  const active = () => {
    const current = model.selectedVariant;
    return ownDesignGeneration === designGeneration && !designController.signal.aborted && `${current?.productUid}:${current?.catalogProductId}:${current?.catalogVariantId}:${current?.shopifyVariantId}` === selectedIdentity;
  };
  const assertActive = () => { if (!active()) throw new DOMException('Preview generation superseded', 'AbortError'); };
  const variantStatus = $('[data-variant-status]');
  if (variantStatus) variantStatus.removeAttribute('data-error');
  const orderError = $('[data-order-error]');
  if (orderError) {
    orderError.hidden = true;
    orderError.textContent = '';
  }
  
  // Immediately set generation status to starting so mockup loading overlay displays on step 4 entry with 0ms delay
  model.providerPreviewStatus = 'starting';
  model.providerPreviewError = '';
  model.providerPreviewUrl = '';
  model.productionFileUrl = '';
  model.temporaryProofUrl = '';
  model.proofUrl = '';

  // Transition immediately to Step 4 as requested by user
  unlockReview();
  setStep(4);
  syncReview();
  setupFinalScenes();
  // Open on the exact, printable proof while the optional product mockup
  // renders in the background.
  showPanel('proof');

  const mobileGenerationStatus = $('[data-mobile-generation-status]');
  if (mobileGenerationStatus) mobileGenerationStatus.hidden = false;
  const proofLoading = $('[data-proof-loading]');
  if (proofLoading) {
    proofLoading.hidden = false;
    startProofLoadingAnimation();
  }
  const proof = $('[data-production-proof]');
  if (proof) {
    proof.hidden = true;
    proof.removeAttribute('src');
  }

  try {
    const cropSync = await model.cropSyncPromise;
    assertActive();
    if (cropSync?.ok === false) throw new Error(cropSync.error || 'The selected crop could not be synchronized. Please apply it again.');
    const state = designState();
    const key = JSON.stringify({ memoryId: model.memoryId, ...state, trimmedSequenceHash: model.appliedTrimmedSequenceHash || null });
    
    const response = await fetch(root.dataset.designUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ memoryId: model.memoryId, design: state, catalogProductId: selected.catalogProductId, catalogVariantId: selected.catalogVariantId, destinationCountry: root.dataset.country || 'US' }),
      signal: designController.signal
    });
    const payload = await response.json().catch(() => ({}));
    assertActive();
    const productionFileUrl = payload.temporaryProofUrl || payload.canonicalPreviewUrl || payload.productionFileUrl || '';
    const proofError = payload.error || (payload.status === 'processing' ? 'The production file is still processing. Please try again shortly.' : `Preview request failed (HTTP ${response.status}).`);
    if (!response.ok || !productionFileUrl || payload.proofState === 'approved') throw new Error(proofError);
    if (model.cropApplied && model.appliedTrimmedSequenceHash) {
      if (!payload.trimmedSequenceHash || payload.trimmedSequenceHash !== model.appliedTrimmedSequenceHash) {
        throw new Error('The server crop does not match the selected section. Please apply the crop again.');
      }
    }
    const providerMedia = payload.providerMedia || {};
    const providerUrls = [providerMedia.previewDefaultUrl, providerMedia.previewThumbnailUrl, ...Object.values(providerMedia.previewUrls || {})].filter(value => typeof value === 'string' && value);
    if (providerUrls.includes(productionFileUrl)) throw new Error('Provider previews cannot be used as the Exact Print Proof.');
    if (payload.production?.productUid && payload.production.productUid !== selected.productUid) throw new Error('The production template does not match the selected Product UID.');
    
    model.productionFileUrl = productionFileUrl;
    model.temporaryProofUrl = productionFileUrl;
    model.proofUrl = '';
    model.approved = false;
    model.productionMetadata = payload.production || null;
    model.productionKey = key;
    
    if (proof) {
      await ensureProductionProofImage(model.productionFileUrl);
      assertActive();
    }
    if (proofLoading) proofLoading.hidden = true;
    stopProofLoadingAnimation();
    if (mobileGenerationStatus) mobileGenerationStatus.hidden = true;
  $('[data-final-tabs]').hidden = false;
  const previewNote = $('[data-preview-note]');
  if (previewNote) {
    previewNote.hidden = false;
    previewNote.textContent = 'Print-ready proof for this exact product. This is the artwork used for production.';
  }
    $('[data-review-production]').textContent = payload.production?.widthPx && payload.production?.heightPx ? `${payload.production.widthPx} × ${payload.production.heightPx}px · temporary exact proof` : 'Temporary exact proof · approval required';
    
    // Request exact Printful product preview now that backend soundwave master proof is verified
    requestExactProductPreview(selected);

    setupFinalScenes();
    updatePurchaseState();
  } catch (error) {
    if (error?.name === 'AbortError' || !active()) return;
    mockupLifecycle.cancel();
    model.providerPreviewStatus = 'unavailable';
    model.providerPreviewError = error.message || 'The production proof could not be generated.';
    setupFinalScenes();
    if (mobileGenerationStatus) mobileGenerationStatus.hidden = true;
    if (proofLoading) proofLoading.hidden = true;
    stopProofLoadingAnimation();
    const msg = error.message || 'The production proof could not be generated.';
    if (orderError) {
      orderError.hidden = false;
      orderError.textContent = msg;
    }
    if (variantStatus) {
      variantStatus.textContent = msg;
      variantStatus.setAttribute('data-error', 'true');
    }
  } finally {
    if (ownDesignGeneration === designGeneration) activeDesignController = null;
    if (button && active()) {
      button.disabled = !model.selectedVariant;
      button.textContent = 'Generate print-ready proof →';
    }
  }
}

const mobileGenerateProof = $('[data-mobile-generate-proof]');
const desktopGenerateProof = $('[data-generate-proof]');
if (mobileGenerateProof && desktopGenerateProof) {
  const syncMobileGenerateProof = () => {
    mobileGenerateProof.disabled = desktopGenerateProof.disabled;
    mobileGenerateProof.textContent = desktopGenerateProof.disabled && /Generating/i.test(desktopGenerateProof.textContent) ? 'Generating…' : 'Generate proof';
  };
  mobileGenerateProof.addEventListener('click', () => { if (!desktopGenerateProof.disabled) desktopGenerateProof.click(); });
  new MutationObserver(syncMobileGenerateProof).observe(desktopGenerateProof, { attributes: true, childList: true, subtree: true });
  syncMobileGenerateProof();
}

async function approveProof() {
  const selected = model.selectedVariant;
  if (!selected?.productUid || !selected?.shopifyVariantId || !model.memoryId || !model.temporaryProofUrl) throw new Error('Generate the exact production proof before approving it.');
  if (!$('[data-confirm-details]').checked || !$('[data-confirm-proof]').checked) throw new Error('Confirm both review statements before approving the exact proof.');
  const approvalContext = captureApprovalContext(selected);
  const state = designState();
  try {
    const response = await fetch(root.dataset.approveUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        memoryId: model.memoryId,
        approved: true,
        productUid: selected.productUid,
        shopifyVariantId: String(selected.shopifyVariantId),
        catalogProductId: selected.catalogProductId,
        catalogVariantId: selected.catalogVariantId,
        destinationCountry: root.dataset.country || 'US',
        approvalTextVersion: model.approvalTextVersion,
        trimStartMs: state.trimStartMs,
        trimEndMs: state.trimEndMs
      })
    });
    const payload = await response.json().catch(() => ({}));
    if (!approvalContextIsCurrent(approvalContext, model.selectedVariant)) throw new DOMException('Approval superseded by a newer design.', 'AbortError');
    const approvedSha256 = String(payload.productionSha256 || '').trim().toLowerCase();
    const approvedByteSize = Number(payload.productionByteSize || 0);
    if (response.ok && payload.status === 'customer_approved' && payload.revisionId && payload.artworkId && /^[a-f0-9]{64}$/.test(approvedSha256) && approvedByteSize > 0) {
      const deliveredProofUrl = String(payload.proofUrl || '').trim();
      model.approved = true;
      model.productionFileUrl = deliveredProofUrl || model.temporaryProofUrl;
      model.proofUrl = deliveredProofUrl || model.temporaryProofUrl;
      model.revisionId = String(payload.revisionId);
      model.artworkId = String(payload.artworkId);
      model.productionSha256 = approvedSha256;
      model.productionByteSize = approvedByteSize;
      model.productionMetadata = payload.production || model.productionMetadata;
      const proof = $('[data-production-proof]');
      if (proof) await ensureProductionProofImage(model.proofUrl);
      const previewNote = $('[data-preview-note]');
      if (previewNote) {
        previewNote.hidden = false;
        previewNote.textContent = 'Approved print-ready proof. This exact artwork is bound to your cart and production order.';
      }
      $('[data-review-production]').textContent = payload.production?.widthPx && payload.production?.heightPx ? `✓ ${payload.production.widthPx} × ${payload.production.heightPx}px · Verified` : '✓ Approved Master Proof';
      setupFinalScenes();
      updatePurchaseState();
      return payload;
    }
    if (!response.ok && !isEditorPreview) {
      const serverMessage = String(payload.error || '').trim();
      const message = response.status === 409 && serverMessage === 'Production proof approval is unavailable'
        ? 'Your print-ready proof is ready. Checkout is temporarily unavailable while we complete final artwork review.'
        : serverMessage || 'The print-ready proof could not be approved. Please try again.';
      throw new Error(message);
    }
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    if (!isEditorPreview) throw err;
    console.warn('Approve endpoint unavailable in theme editor preview; using a local-only proof signature', err);
  }
  if (!isEditorPreview) throw new Error('The print-ready proof could not be approved. Please try again.');

  // Resilient fallback for Shopify theme editor preview only.
  model.approved = true;
  model.productionFileUrl = model.temporaryProofUrl || '';
  model.proofUrl = model.temporaryProofUrl || '';
  model.revisionId = model.revisionId || `rev_${Date.now()}`;
  model.artworkId = model.artworkId || `art_${Date.now()}`;
  model.productionSha256 = model.productionSha256 || '0'.repeat(64);
  const previewNote = $('[data-preview-note]');
  if (previewNote) {
    previewNote.hidden = false;
    previewNote.textContent = 'Approved print-ready proof. This exact artwork is bound to your cart and production order.';
  }
  $('[data-review-production]').textContent = '✓ Approved Master Proof';
  setupFinalScenes();
  updatePurchaseState();
  return { status: 'customer_approved' };
}

function setupFinalScenes() {
  const selected = model.selectedVariant || {};
  const staticGallery = Array.isArray(model.providerGallery) ? model.providerGallery.filter(item => item && item.url) : [];
  const isGenerating = ['starting', 'processing'].includes(model.providerPreviewStatus);
  const isFailed = ['delayed', 'unavailable'].includes(model.providerPreviewStatus);
  const hasPersonalized = Boolean(model.providerPreviewUrl);

  // Product tab: always visible once user reaches step 4 (generating or ready)
  const productTab = $('[data-view="product"]');
  const hasProviderPreview = hasPersonalized || isGenerating || isFailed;
  if (productTab) {
    productTab.disabled = !hasProviderPreview;
    productTab.classList.toggle('is-disabled', !hasProviderPreview);
    productTab.hidden = !hasProviderPreview;
  }

  // Loading overlay: shown while Printful is generating the mockup
  const loadingOverlay = $('[data-product-loading]');
  if (loadingOverlay) {
    loadingOverlay.hidden = !isGenerating;
    if (isGenerating) {
      startMockupLoadingAnimation();
    } else {
      stopMockupLoadingAnimation();
    }
  }

  const productAsset = $('[data-product-asset]');

  // While generating: hide the static gallery image entirely — show spinner only
  if (isGenerating) {
    if (productAsset) { productAsset.hidden = true; productAsset.removeAttribute('src'); }
    const thumbnails = $('[data-product-thumbnails]');
    if (thumbnails) thumbnails.replaceChildren();
    const statusEl = $('[data-product-preview-status]');
    if (statusEl) statusEl.textContent = 'Generating personalized preview…';
    const previous = $('[data-product-previous]');
    const next = $('[data-product-next]');
    if (previous) previous.hidden = true;
    if (next) next.hidden = true;
    const previewRetry = $('[data-product-preview-retry]');
    if (previewRetry) previewRetry.hidden = true;
    const productPreviewNote = $('[data-product-preview-note]');
    if (productPreviewNote) productPreviewNote.textContent = 'Rendering realistic product preview with your soundwave.';
    return;
  }

  // If the provider takes longer than expected, keep the exact proof available and
  // show a clearly-labeled product reference instead of an empty white panel.
  // The personalized mockup still replaces this reference as soon as it arrives.
  const referenceProductUrl = String(
    selected.productAssetUrl || staticGallery.find(item => item.purpose !== 'indoor-scene')?.url || ''
  ).trim();
  const gallery = hasPersonalized
    ? [{ url: model.providerPreviewUrl, label: 'Your personalized preview', purpose: 'personalized' }]
    : referenceProductUrl
      ? [{ url: referenceProductUrl, label: 'Product reference', purpose: 'reference' }]
      : [];
  if (productAsset) {
    productAsset.hidden = !gallery.length;
    if (!gallery.length) {
      productAsset.removeAttribute('src');
      productAsset.alt = '';
    }
  }

  if (model.providerPreviewUrl) {
    model.providerGalleryActiveIndex = 0;
  } else {
    const previousUrl = productAsset ? (productAsset.getAttribute('src') || '') : '';
    const preservedIndex = gallery.findIndex(item => item.url === previousUrl);
    model.providerGalleryActiveIndex = Math.max(0, Math.min(gallery.length - 1, preservedIndex >= 0 ? preservedIndex : model.providerGalleryActiveIndex || 0));
  }
  const thumbnails = $('[data-product-thumbnails]');
  if (thumbnails) thumbnails.replaceChildren();
  const showGalleryItem = index => {
    if (!gallery.length) return;
    model.providerGalleryActiveIndex = (index + gallery.length) % gallery.length;
    const item = gallery[model.providerGalleryActiveIndex];
    if (productAsset) {
      productAsset.src = item.url;
      productAsset.alt = item.label || 'Selected Printful product reference';
    }
    if (thumbnails) {
      [...thumbnails.children].forEach((child, childIndex) => child.setAttribute('aria-current', childIndex === model.providerGalleryActiveIndex ? 'true' : 'false'));
    }
    const statusEl = $('[data-product-preview-status]');
    if (statusEl) statusEl.textContent = `${item.label || 'Product view'} · ${model.providerGalleryActiveIndex + 1} of ${gallery.length}`;
  };
  gallery.forEach((item, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', item.label || `Product view ${index + 1}`);
    button.setAttribute('aria-current', index === model.providerGalleryActiveIndex ? 'true' : 'false');
    const image = document.createElement('img');
    image.src = item.url;
    image.alt = '';
    image.loading = index ? 'lazy' : 'eager';
    button.append(image);
    button.addEventListener('click', () => {
      showGalleryItem(index);
    });
    if (thumbnails) thumbnails.append(button);
  });
  if (hasProviderPreview) showGalleryItem(model.providerGalleryActiveIndex);
  else {
    if (productAsset) { productAsset.removeAttribute('src'); productAsset.alt = ''; }
    const statusEl = $('[data-product-preview-status]');
    if (statusEl) statusEl.textContent = '';
  }
  const productStatus = $('[data-product-preview-status]');
  if (productStatus && !hasPersonalized && model.providerPreviewStatus === 'delayed') {
    productStatus.textContent = referenceProductUrl
      ? 'Still rendering your personalized product preview. Showing the product reference meanwhile.'
      : 'Still rendering your personalized product preview. Your exact print-ready proof remains ready.';
  } else if (productStatus && !hasPersonalized && model.providerPreviewStatus === 'unavailable') {
    productStatus.textContent = referenceProductUrl
      ? 'Personalized product preview is temporarily unavailable. Showing the product reference meanwhile.'
      : 'Personalized product preview is temporarily unavailable. Your exact print-ready proof remains ready.';
  }
  const previous = $('[data-product-previous]');
  const next = $('[data-product-next]');
  if (previous && next) {
    previous.hidden = next.hidden = gallery.length < 2;
    previous.onclick = () => showGalleryItem(model.providerGalleryActiveIndex - 1);
    next.onclick = () => showGalleryItem(model.providerGalleryActiveIndex + 1);
  }
  const panel = $('[data-view-panel="product"]');
  if (panel) {
    panel.onpointerdown = event => { touchX = event.clientX; };
    panel.onpointerup = event => {
      const endX = event.clientX;
      if (touchX != null && Number.isFinite(endX) && Math.abs(endX - touchX) > 40) showGalleryItem(model.providerGalleryActiveIndex + (endX < touchX ? 1 : -1));
      touchX = null;
    };
  }
  const previewRetry = $('[data-product-preview-retry]');
  if (previewRetry) {
    previewRetry.hidden = !['delayed', 'unavailable'].includes(model.providerPreviewStatus);
    previewRetry.onclick = () => {
      if (!model.selectedVariant) return;
      if (!model.productionFileUrl) generateProof();
      else if (model.providerPreviewStatus === 'delayed' && model.providerPreviewFingerprint) resumeExactProductPreview();
      else requestExactProductPreview(model.selectedVariant);
    };
  }
  const productArtwork = $('[data-product-artwork]');
  if (productArtwork) productArtwork.style.backgroundImage = '';
  const productPreviewNote = $('[data-product-preview-note]');
  if (productPreviewNote) productPreviewNote.textContent = model.providerPreviewUrl
    ? 'Printful personalized mockup generated for this selected product and artwork.'
    : model.providerPreviewStatus === 'delayed'
      ? (referenceProductUrl ? 'Product reference — your personalized mockup is still rendering.' : 'The personalized product preview is taking longer than usual. Your exact print proof remains ready.')
      : (referenceProductUrl ? 'Product reference — personalized mockup is temporarily unavailable.' : 'A personalized Printful product preview is not available yet.');
  const roomTab = $('[data-view="room"]');
  const staticRoom = staticGallery.find(item => item.purpose === 'indoor-scene')?.url || '';
  const hasRoomPreview = Boolean(model.providerRoomUrl || staticRoom);
  if (roomTab) {
    roomTab.disabled = !hasRoomPreview;
    roomTab.classList.toggle('is-disabled', !hasRoomPreview);
    roomTab.hidden = !hasRoomPreview;
  }
  const room = $('[data-view-panel="room"]');
  if (room) room.style.backgroundImage = hasRoomPreview ? `url("${model.providerRoomUrl || staticRoom}")` : '';
  const roomArtwork = $('[data-room-artwork]');
  if (roomArtwork) roomArtwork.style.backgroundImage = '';
}

function syncReview() {
  const selected = model.selectedVariant || {};
  const state = designState();
  $('[data-review-product]').textContent = selected.familyLabel || selected.familyKey || '—';
  $('[data-review-specs]').textContent = selected.summary || Object.values(selected.attributes || {}).concat([selected.orientation, selected.size]).filter(Boolean).join(' · ') || '—';
  $('[data-review-layout]').textContent = `${state.artworkLayout.charAt(0).toUpperCase()}${state.artworkLayout.slice(1)} · Fine line`;
  $('[data-review-message]').textContent = state.title || state.kicker || state.date || 'No text added';
  updatePurchaseState();
}

function updatePurchaseState() {
  const published = model.selectedVariant?.published !== false;
  const state = designState();
  const currentProductionKey = JSON.stringify({ memoryId: model.memoryId, ...state, trimmedSequenceHash: model.appliedTrimmedSequenceHash || null });
  const proofMatchesCurrentDesign = Boolean(model.productionKey && model.productionKey === currentProductionKey);
  const button = $('[data-add-cart]');
  const quarantined = model.cartAttemptQuarantined || hasCartAttemptQuarantine({
    memoryId: model.memoryId,
    artworkId: model.artworkId,
    revisionId: model.revisionId,
    productionSha256: model.productionSha256,
    productUid: model.selectedVariant?.productUid || '',
    shopifyVariantId: model.selectedVariant?.shopifyVariantId || '',
    approvalVersion: model.approvalTextVersion
  });
  if (button) {
    button.disabled = quarantined || !(published && model.temporaryProofUrl && proofMatchesCurrentDesign && $('[data-confirm-details]').checked && $('[data-confirm-proof]').checked);
    button.textContent = quarantined ? 'Open your cart to continue' : 'Approve & Add to Cart →';
  }
  if (!published && model.selectedVariant) {
    const errorNode = $('[data-order-error]');
    if (errorNode) {
      errorNode.hidden = false;
      errorNode.textContent = 'This configuration is connected to production, but its Shopify variant is not published yet. Preview is available; checkout stays locked.';
    }
  }
}

function localeCartUrl(path) {
  return new URL(path, new URL(window.Shopify?.routes?.root || '/', location.origin)).toString();
}

function purchaseDeviceCategory() {
  return window.matchMedia('(max-width: 760px)').matches ? 'mobile' : 'desktop';
}

function emitPurchaseTiming(stage, { startedAt = null, result = 'success' } = {}) {
  const durationMs = startedAt == null ? 0 : Math.max(0, Math.round(performance.now() - startedAt));
  const detail = { stage, durationMs, result, deviceCategory: purchaseDeviceCategory() };
  root.dispatchEvent(new CustomEvent('voicewave:purchase-timing', { detail }));
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: 'voicewave_purchase_timing', purchase_stage: stage, duration_ms: durationMs, purchase_result: result, device_category: detail.deviceCategory });
  }
  return durationMs;
}

function setPurchaseStage(message, { buttonText = message, disabled = true } = {}) {
  const progress = $('[data-order-progress]');
  if (progress) {
    progress.hidden = !message;
    progress.textContent = message || '';
  }
  const button = $('[data-add-cart]');
  if (button) {
    button.disabled = disabled;
    button.textContent = buttonText;
  }
}

function cartLineProperty(item, key) {
  const properties = item?.properties;
  if (Array.isArray(properties)) {
    const entry = properties.find(property => String(property?.name || property?.key || '') === key);
    return entry ? String(entry.value ?? '') : '';
  }
  if (properties && typeof properties === 'object') return String(properties[key] ?? '');
  return '';
}

function cartIdentityStorageKey(prefix, identity) {
  return `${prefix}:${[
    identity?.memoryId,
    identity?.artworkId,
    identity?.revisionId,
    identity?.productionSha256,
    identity?.productUid,
    identity?.shopifyVariantId,
    identity?.approvalVersion
  ].map(value => encodeURIComponent(String(value || ''))).join(':')}`;
}

function captureApprovalContext(selected) {
  return {
    generation: designGeneration,
    selectedIdentity: `${selected?.productUid}:${selected?.catalogProductId}:${selected?.catalogVariantId}:${selected?.shopifyVariantId}`,
    proofUrl: model.temporaryProofUrl,
    productionKey: model.productionKey
  };
}

function approvalContextIsCurrent(context, selected) {
  return context?.generation === designGeneration
    && context.selectedIdentity === `${selected?.productUid}:${selected?.catalogProductId}:${selected?.catalogVariantId}:${selected?.shopifyVariantId}`
    && context.proofUrl === model.temporaryProofUrl
    && context.productionKey === model.productionKey;
}

function hasCartAttemptQuarantine(identity) {
  if (!identity?.artworkId || !identity?.revisionId) return false;
  try { return Boolean(localStorage.getItem(cartIdentityStorageKey('voicewave-cart-quarantine', identity))); }
  catch (_) { return true; }
}

function markCartAttemptQuarantine(identity) {
  model.cartAttemptQuarantined = true;
  try {
    localStorage.setItem(cartIdentityStorageKey('voicewave-cart-quarantine', identity), JSON.stringify({ createdAt: Date.now() }));
  } catch (_) { /* in-memory fail-closed state still prevents a retry */ }
}

function clearCartAttemptQuarantine(identity) {
  model.cartAttemptQuarantined = false;
  try { localStorage.removeItem(cartIdentityStorageKey('voicewave-cart-quarantine', identity)); }
  catch (_) { /* a future exact precheck will remain fail-closed */ }
}

function exactCartLineMatches(item, identity) {
  const variantId = String(item?.variant_id ?? item?.id ?? '');
  return variantId === String(identity.shopifyVariantId)
    && cartLineProperty(item, 'Memory ID') === identity.memoryId
    && cartLineProperty(item, 'Artwork ID') === identity.artworkId
    && cartLineProperty(item, 'Production Revision ID') === identity.revisionId
    && cartLineProperty(item, 'Artwork SHA-256').toLowerCase() === identity.productionSha256
    && cartLineProperty(item, 'POD Product UID') === identity.productUid
    && cartLineProperty(item, 'Shopify Variant ID') === String(identity.shopifyVariantId)
    && cartLineProperty(item, 'Approval Version') === identity.approvalVersion;
}

function findExactAddedLine(payload, identity) {
  const addedItems = Array.isArray(payload?.items) ? payload.items : [];
  return addedItems.find(item => exactCartLineMatches(item, identity)) || null;
}

async function readCartSnapshot(identity) {
  const response = await fetch(localeCartUrl('cart.js'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store'
  });
  if (!response.ok) throw new Error('We could not verify your cart safely. Please try again.');
  const cart = await response.json();
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return { cart, line: items.find(item => exactCartLineMatches(item, identity)) || null };
}

async function readExactCartLine(identity) {
  return (await readCartSnapshot(identity)).line;
}

async function recoverAmbiguousCart({ identity, purchaseStartedAt, recoveryStartedAt, unconfirmedMessage }) {
  let snapshot;
  try {
    snapshot = await readCartSnapshot(identity);
  } catch (_) {
    markCartAttemptQuarantine(identity);
    emitPurchaseTiming('cart_recovery', { startedAt: recoveryStartedAt, result: 'unconfirmed' });
    throw new Error(unconfirmedMessage);
  }
  emitPurchaseTiming('cart_recovery', { startedAt: recoveryStartedAt, result: snapshot.line ? 'recovered' : 'absent' });
  if (!snapshot.line) {
    markCartAttemptQuarantine(identity);
    setPurchaseStage('We’re confirming your memory…', { buttonText: 'Open your cart to continue', disabled: true });
    return false;
  }
  clearCartAttemptQuarantine(identity);
  await settleSuccessfulCartUi({
    action: 'add',
    identity,
    cart: snapshot.cart,
    itemCount: Number(snapshot.cart?.item_count || 0),
    line: snapshot.line,
    purchaseStartedAt
  });
  return true;
}

function cartSectionIds() {
  return [...new Set([...document.querySelectorAll('cart-items-component')]
    .map(item => item instanceof HTMLElement ? String(item.dataset.sectionId || '').trim() : '')
    .filter(Boolean))];
}

function publishCartLinesUpdate({ action = 'add', identity, payload = null, cart = null, itemCount = 0, line = null }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const target = document.querySelector('theme-drawer#cart-drawer') || root;
  const lines = action === 'add'
    ? [{ merchandiseId: identity.shopifyVariantId, quantity: 1 }]
    : [{ id: String(line?.key || line?.id || ''), quantity: Number(line?.quantity || 1) }];
  target.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise }));
  deferred.resolve({
    cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
    detail: {
      ...(payload?.sections && typeof payload.sections === 'object' ? { sections: payload.sections } : {}),
      ...(cart && Array.isArray(cart.items) ? { items: cart.items } : {}),
      source: 'voicewave-customizer',
      sourceId: 'vm-builder',
      itemCount: Number(itemCount || cart?.item_count || 0),
      didError: false
    }
  });
  return deferred.promise;
}

async function openNativeCartDrawer(purchaseStartedAt) {
  const drawerStartedAt = performance.now();
  const openCart = window.Shopify?.actions?.openCart;
  if (typeof openCart === 'function') {
    await openCart();
  } else {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else {
      location.href = localeCartUrl('cart');
      return;
    }
  }
  emitPurchaseTiming('drawer_open', { startedAt: drawerStartedAt });
  if (purchaseStartedAt != null) emitPurchaseTiming('purchase_to_drawer', { startedAt: purchaseStartedAt });
}

async function settleSuccessfulCartUi({ action = 'add', identity, payload = null, cart = null, itemCount = 0, line = null, purchaseStartedAt }) {
  await publishCartLinesUpdate({ action, identity, payload, cart, itemCount, line });
  model.cartViewReady = true;
  setPurchaseStage('Added ✓', { buttonText: 'Added ✓ · View cart', disabled: false });
  if (purchaseStartedAt != null) emitPurchaseTiming('cart_success_visible', { startedAt: purchaseStartedAt });
  await new Promise(resolve => requestAnimationFrame(resolve));
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  const autoOpenDelegated = action === 'add'
    && Boolean(drawer?.querySelector('cart-drawer-component[auto-open]'))
    && typeof drawer?.open === 'function';
  if (!autoOpenDelegated) await openNativeCartDrawer(purchaseStartedAt);
}

async function withCartIdentityLock(identity, operation) {
  const lockName = `voicewave-cart:${identity.artworkId}:${identity.revisionId}`;
  if (navigator.locks?.request) return navigator.locks.request(lockName, { mode: 'exclusive' }, operation);

  const storageKey = `voicewave-cart-lock:${identity.artworkId}:${identity.revisionId}`;
  const token = crypto.randomUUID();
  const leaseDurationMs = 10000;
  const heartbeatIntervalMs = 2500;
  const claimSettleMs = 100;
  const deadline = Date.now() + 5000;
  while (Date.now() < deadline) {
    let current;
    try { current = JSON.parse(localStorage.getItem(storageKey) || 'null'); }
    catch (_) { throw new Error('Cart synchronization is unavailable. Please retry with only one store tab open.'); }
    if (current?.token && Number(current.expiresAt || 0) <= Date.now()) {
      try {
        const latest = JSON.parse(localStorage.getItem(storageKey) || 'null');
        if (latest?.token === current.token && Number(latest.expiresAt || 0) <= Date.now()) localStorage.removeItem(storageKey);
      } catch (_) { /* the next attempt will fail closed again */ }
      throw new Error('A previous cart attempt expired before it could be confirmed. Review your cart, then try again.');
    }
    if (!current?.token) {
      let confirmed;
      try {
        localStorage.setItem(storageKey, JSON.stringify({ token, expiresAt: Date.now() + leaseDurationMs }));
        // Web Storage has no compare-and-set. Let simultaneous claimants publish
        // before confirming one stable owner; a loser fails closed before add.js.
        await wait(claimSettleMs);
        confirmed = JSON.parse(localStorage.getItem(storageKey) || 'null');
      } catch (_) {
        throw new Error('Cart synchronization is unavailable. Please retry with only one store tab open.');
      }
      if (confirmed?.token === token) {
        let leaseLost = false;
        const heartbeat = setInterval(() => {
          try {
            const latest = JSON.parse(localStorage.getItem(storageKey) || 'null');
            if (latest?.token !== token) { leaseLost = true; return; }
            localStorage.setItem(storageKey, JSON.stringify({ token, expiresAt: Date.now() + leaseDurationMs }));
            const renewed = JSON.parse(localStorage.getItem(storageKey) || 'null');
            if (renewed?.token !== token) leaseLost = true;
          } catch (_) { leaseLost = true; }
        }, heartbeatIntervalMs);
        try {
          const result = await operation();
          let latest;
          try { latest = JSON.parse(localStorage.getItem(storageKey) || 'null'); }
          catch (_) { latest = null; }
          if (leaseLost || latest?.token !== token || Number(latest.expiresAt || 0) <= Date.now()) {
            throw new Error('Cart synchronization lease could not be renewed. Review your cart, then try again.');
          }
          return result;
        }
        finally {
          clearInterval(heartbeat);
          try {
            const latest = JSON.parse(localStorage.getItem(storageKey) || 'null');
            if (latest?.token === token) localStorage.removeItem(storageKey);
          } catch (_) { /* lock expires automatically */ }
        }
      }
    }
    await wait(80);
  }
  throw new Error('This exact artwork is already being added in another tab. Please wait a moment and try again.');
}

async function addToCart(purchaseStartedAt = performance.now()) {
  const errorNode = $('[data-order-error]');
  if (errorNode) errorNode.hidden = true;
  const selected = model.selectedVariant;
  if (!selected?.shopifyVariantId) throw new Error('Please select a product variant to continue.');
  const approvedSha256 = String(model.productionSha256 || '').trim().toLowerCase();
  if (!model.approved || !model.memoryId || !model.artworkId || !model.revisionId || !/^[a-f0-9]{64}$/.test(approvedSha256) || approvedSha256 === '0'.repeat(64)) {
    throw new Error('Your print-ready proof must be approved again before it can be added to the cart.');
  }
  const printful = /^printful:/i.test(String(selected.productUid || ''));
  const state = designState();
  const properties = {
    'Memory ID': model.memoryId,
    'Artwork ID': model.artworkId,
    'Production Revision ID': model.revisionId,
    'Artwork SHA-256': approvedSha256,
    'POD Provider': printful ? 'printful' : 'gelato',
    'POD Product UID': selected.productUid || '',
    'Shopify Variant ID': String(selected.shopifyVariantId),
    'Approval Version': model.approvalTextVersion,
    Message: state.title,
    Heading: state.kicker,
    'Date or place': state.date,
    'Wave color': state.waveColor,
    Background: state.background,
    'Text color': state.textColor,
    'Font style': state.fontStyle,
    'Artwork layout': state.artworkLayout,
    'Waveform style': state.waveformStyle,
    'Trim start ms': String(state.trimStartMs),
    'Trim end ms': String(state.trimEndMs)
  };
  for (const [key, value] of Object.entries(selected.attributes || {})) properties[key] = String(value);
  const identity = {
    memoryId: String(model.memoryId),
    artworkId: String(model.artworkId),
    revisionId: String(model.revisionId),
    productionSha256: approvedSha256,
    productUid: String(selected.productUid || ''),
    shopifyVariantId: String(selected.shopifyVariantId),
    approvalVersion: String(model.approvalTextVersion)
  };

  await withCartIdentityLock(identity, async () => {
    const precheckStartedAt = performance.now();
    const precheck = await readCartSnapshot(identity);
    emitPurchaseTiming('cart_precheck', { startedAt: precheckStartedAt });
    // If a prior click/tab/network attempt already added this immutable Artwork,
    // do not increment the line again. Quantity changes belong in the cart UI.
    if (precheck.line) {
      clearCartAttemptQuarantine(identity);
      await settleSuccessfulCartUi({ action: 'update', identity, cart: precheck.cart, itemCount: Number(precheck.cart?.item_count || 0), line: precheck.line, purchaseStartedAt });
      return;
    }

    if (hasCartAttemptQuarantine(identity)) {
      model.cartAttemptQuarantined = true;
      setPurchaseStage('We’re confirming your memory…', { buttonText: 'Open your cart to continue', disabled: true });
      throw new Error('A previous cart request is still being confirmed. Open your cart before trying again.');
    }

    let response;
    let payload = {};
    const addStartedAt = performance.now();
    try {
      response = await fetch(localeCartUrl('cart/add.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(selected.shopifyVariantId), quantity: 1, properties }],
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname
        })
      });
      payload = await response.json().catch(() => ({}));
      emitPurchaseTiming('cart_add', { startedAt: addStartedAt, result: response.ok ? 'success' : 'http_error' });
    } catch (networkError) {
      emitPurchaseTiming('cart_add', { startedAt: addStartedAt, result: 'network_error' });
      const recoveryStartedAt = performance.now();
      const recovered = await recoverAmbiguousCart({ identity, purchaseStartedAt, recoveryStartedAt, unconfirmedMessage: 'Your cart update could not be confirmed safely. Open the cart before trying again.' });
      if (recovered) return;
      throw new Error(networkError?.message || 'The cart request was interrupted before this artwork could be verified. Please try again.');
    }

    if (!response.ok) {
      const recoveryStartedAt = performance.now();
      const recovered = await recoverAmbiguousCart({ identity, purchaseStartedAt, recoveryStartedAt, unconfirmedMessage: 'Your cart update could not be confirmed safely. Open the cart before trying again.' });
      if (recovered) return;
      throw new Error(payload.description || payload.message || 'The item could not be added to the cart.');
    }

    const addedLine = findExactAddedLine(payload, identity);
    if (addedLine) {
      clearCartAttemptQuarantine(identity);
      await settleSuccessfulCartUi({ action: 'add', identity, payload, itemCount: Number(precheck.cart?.item_count || 0) + 1, line: addedLine, purchaseStartedAt });
      return;
    }

    // A successful response without the exact immutable line is ambiguous.
    // Read back only this path; never issue a second add.
    const recoveryStartedAt = performance.now();
    const recovered = await recoverAmbiguousCart({ identity, purchaseStartedAt, recoveryStartedAt, unconfirmedMessage: 'Shopify accepted the cart request, but this exact artwork could not be verified safely. Open the cart before trying again.' });
    if (!recovered) throw new Error('Shopify accepted the cart request, but this exact artwork could not be verified yet. Open the cart before trying again.');
  });
}

async function openSettledCart() {
  if (!model.cartViewReady) return false;
  await openNativeCartDrawer();
  return true;
}

async function handleAddCartClick() {
  if (await openSettledCart()) return;
  await approveAndAddToCart();
}

async function approveAndAddToCart() {
  const purchaseStartedAt = performance.now();
  emitPurchaseTiming('purchase_click', { startedAt: purchaseStartedAt });
  const errorNode = $('[data-order-error]');
  if (errorNode) errorNode.hidden = true;
  try {
    if (!model.approved) {
      setPurchaseStage('Securing your artwork…');
      const approvalStartedAt = performance.now();
      await approveProof();
      emitPurchaseTiming('approval', { startedAt: approvalStartedAt });
      setPurchaseStage('Artwork secured ✓');
      await new Promise(resolve => requestAnimationFrame(resolve));
    }
    setPurchaseStage('Adding to cart…');
    await addToCart(purchaseStartedAt);
  } catch (error) {
    emitPurchaseTiming('cart_error', { startedAt: purchaseStartedAt, result: 'error' });
    const progress = $('[data-order-progress]');
    if (progress) progress.hidden = true;
    if (errorNode) {
      errorNode.hidden = false;
      errorNode.textContent = error.message || 'The item could not be added to the cart.';
    }
    updatePurchaseState();
  }
}

$('#vm-media-file').addEventListener('change', event => uploadFile(event.target.files?.[0]));
$('[data-cancel-upload]').addEventListener('click', cancelCurrentUpload);
const dropZone = $('[data-drop-zone]');
['dragenter', 'dragover'].forEach(type => dropZone.addEventListener(type, event => { event.preventDefault(); dropZone.classList.add('is-dragging'); }));
['dragleave', 'drop'].forEach(type => dropZone.addEventListener(type, event => { event.preventDefault(); dropZone.classList.remove('is-dragging'); }));
dropZone.addEventListener('drop', event => uploadFile(event.dataTransfer?.files?.[0]));

$('[data-record]').addEventListener('click', async event => {
  if (recorder?.state === 'recording') { recorder.stop(); event.currentTarget.textContent = '● Record now'; return; }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recordingStream = stream;
    chunks = [];
    recorder = new MediaRecorder(stream);
    recorder.ondataavailable = item => { if (item.data.size) chunks.push(item.data); };
    recorder.onstop = () => {
      stream.getTracks().forEach(track => track.stop());
      recordingStream = null;
      uploadFile(new File(chunks, 'voice-recording.webm', { type: recorder.mimeType || 'audio/webm' }));
    };
    recorder.start();
    event.currentTarget.textContent = '■ Stop recording';
    setUploadStatus('processing', 'Recording now…', 'Speak naturally. Select “Stop recording” when you are finished.');
  } catch (_) { setUploadStatus('error', 'Microphone access is needed', 'Allow microphone access in your browser, then try recording again.'); }
});

$('[data-play]').addEventListener('click', () => {
  if (!waveSurfer) return;
  if (!waveSurfer.isPlaying()) waveSurfer.setTime(model.duration * (model.isTrimming ? model.trimStart : model.appliedTrimStart));
  waveSurfer.playPause();
});
$('[data-seek]').addEventListener('input', event => waveSurfer?.setTime(model.duration * Number(event.target.value)));
$$('[data-crop-start],[data-crop-end]').forEach(input => input.addEventListener('input', () => syncCrop(input)));
$('[data-edit-trim]').addEventListener('click', showTrimEditor);
$('[data-apply-trim]').addEventListener('click', () => { void applyCrop(); });
$('[data-cancel-trim]').addEventListener('click', () => {
  model.trimStart = model.appliedTrimStart;
  model.trimEnd = model.appliedTrimEnd;
  showArtworkResult();
});
$('[data-use-full]').addEventListener('click', useFullRecording);

$$('[data-title],[data-kicker],[data-date],[data-color],[data-bg],[data-text-color],[data-auto-text],input[name="vm-font"]').forEach(input => input.addEventListener('input', syncDesignPreview));
$$('input[name="vm-artwork-layout"]').forEach(input => input.addEventListener('change', event => {
  model.artworkLayout = event.target.value;
  syncDesignPreview();
}));
// Legacy palette handler handled by vm-customizer-v4.js

$$('[data-next]').forEach(button => button.addEventListener('click', () => {
  const step = Number(button.dataset.next);
  if (step === 3) unlockProduct();
  setStep(step);
}));
$$('[data-back]').forEach(button => button.addEventListener('click', () => setStep(Number(button.dataset.back))));
$$('[data-edit-step]').forEach(button => button.addEventListener('click', () => setStep(Number(button.dataset.editStep))));
$$('[data-step-nav]').forEach(button => button.addEventListener('click', () => { if (!button.disabled) setStep(Number(button.dataset.stepNav)); }));

function applyVariantSelection(variant) {
  const next = variant || null;
  if (next === model.selectedVariant) return;
  model.selectedVariant = next;
  invalidateProduction();
  const selected = model.selectedVariant;
  model.providerGallery = Array.isArray(selected?.gallery) ? selected.gallery : [];
  model.providerPreviewStatus = model.providerGallery.length ? 'reference' : 'idle';
  $('[data-generate-proof]').disabled = !selected;
  const selectionSummary = $('[data-selection-summary]');
  if (selectionSummary) selectionSummary.hidden = !selected;
  const selectedSummaryBox = $('[data-selected-summary-box]');
  if (selectedSummaryBox) selectedSummaryBox.hidden = !selected;
  if (selected) {
    const selectionName = $('[data-selection-name]');
    const selectionDetails = $('[data-selection-details]');
    if (selectionName) selectionName.textContent = selected.familyLabel || selected.familyKey;
    if (selectionDetails) selectionDetails.textContent = selected.summary || Object.values(selected.attributes || {}).concat([selected.orientation, selected.size]).filter(Boolean).join(' · ');
    const selectedTitle = $('[data-selected-product-title]');
    const selectedSub = $('[data-selected-product-sub]');
    const selectedPrice = $('[data-selected-product-price]');
    if (selectedTitle) selectedTitle.textContent = selected.familyLabel || selected.familyKey || 'Selected product';
    if (selectedSub) selectedSub.textContent = selected.summary || Object.values(selected.attributes || {}).concat([selected.orientation, selected.size]).filter(Boolean).join(' · ') || 'Product configuration ready';
    if (selectedPrice) selectedPrice.textContent = money(selected.retailPrice);
    applyBoardGeometry(model.step);
  }
  const total = Number(selected?.retailPrice || 0);
  let compare = Number(selected?.compareAtPrice || 0);
  if (!compare || compare <= total) {
    // Standard luxury discount comparison (~20% off original retail)
    compare = total > 0 ? Math.round((total * 1.25) * 100) / 100 : 0;
  }
  $$('[data-total]').forEach(node => { node.textContent = money(total); });
  $$('[data-price-wrap]').forEach(node => { node.hidden = model.step < 3 || !total; });
  $$('[data-compare]').forEach(node => { node.hidden = !(compare > total); node.textContent = compare > total ? money(compare) : ''; });
  const selectedCompare = $('[data-selected-product-compare]');
  if (selectedCompare) {
    selectedCompare.hidden = !(compare > total);
    selectedCompare.textContent = compare > total ? money(compare) : '';
  }
}

root.addEventListener('vm:variant-change', event => applyVariantSelection(event.detail));
root.addEventListener('vm:variant-ready', event => applyVariantSelection(event.detail));
root.addEventListener('vm:variant-request', () => {
  const snapshot = root.voiceWaveVariantSnapshot || window.VM_SELECTED_VARIANT || null;
  root.dispatchEvent(new CustomEvent('vm:variant-ready', { detail: snapshot }));
});
const initialVariantSnapshot = root.voiceWaveVariantSnapshot || window.VM_SELECTED_VARIANT || null;
if (initialVariantSnapshot || root.dataset.variantReady === 'true') applyVariantSelection(initialVariantSnapshot);

$('[data-generate-proof]').addEventListener('click', generateProof);
$$('[data-view]').forEach(button => button.addEventListener('click', () => { if (!button.disabled) showPanel(button.dataset.view); }));
$$('[data-confirm-details],[data-confirm-proof]').forEach(input => input.addEventListener('change', updatePurchaseState));
$('[data-add-cart]').addEventListener('click', handleAddCartClick);
$('[data-guide-open]').addEventListener('click', () => $('[data-guide]').showModal());
window.addEventListener('resize', () => requestAnimationFrame(() => {
  renderArtworkWaveform();
  renderLayoutPreviews();
  if (model.step === 4 && currentProofImageUrl()) {
    ensureProductionProofImage().catch(reportProofImageError);
  }
}));

syncCrop();
setupTrimEditor();
syncDesignPreview();
setStep(1);

// Shopify's theme editor has no customer upload, but operators still need to
// inspect all four panels and edit their copy/settings. This deterministic
// fixture is editor-only; it never calls upload, peaks, design, mockup, quote,
// or checkout endpoints and never changes the production renderer contract.
function installEditorPreview() {
  if (!isEditorPreview || root.dataset.editorDemo === 'true') return;
  const demoPairs = Array.from({ length: 96 }, (_, index) => {
    const phase = index / 95 * Math.PI * 8;
    const envelope = .22 + .78 * Math.abs(Math.sin(index / 95 * Math.PI * 2.6));
    const pulse = Math.abs(Math.sin(phase) * .68 + Math.sin(phase * 2.7) * .22);
    const amplitude = Math.min(.98, .08 + envelope * pulse);
    return { min: Number((-amplitude).toFixed(4)), max: Number(amplitude.toFixed(4)) };
  });
  const demoVariant = {
    productUid: 'theme-editor-demo-canvas',
    catalogProductId: 0,
    catalogVariantId: 0,
    shopifyVariantId: '0',
    retailPrice: 129,
    compareAtPrice: 159,
    published: true,
    familyKey: 'canvas',
    familyLabel: 'Canvas Prints',
    attributes: { ProductStyle: 'Canvas Print', orientation: 'square', size: '10 × 10 in' },
    orientation: 'square',
    size: '10 × 10 in',
    faceMm: { width: 254, height: 254 },
    summary: 'Canvas Print · 10 × 10 in · Digital',
    gallery: []
  };
  root.dataset.editorDemo = 'true';
  model.memoryId = 'theme-editor-demo';
  model.mediaUrl = '';
  model.peaks = demoPairs.map(pair => (pair.min + pair.max) / 2);
  model.canonicalPeaks = demoPairs;
  model.canonicalMinMax = demoPairs;
  model.duration = 3.82;
  model.durationMs = 3820;
  model.sourceDuration = 3.82;
  model.appliedTrimStart = 0;
  model.appliedTrimEnd = 1;
  model.appliedCanonicalPeaks = [];
  model.appliedCanonicalMinMax = [];
  model.appliedTrimmedSequenceHash = '';
  model.cropApplied = false;
  model.isTrimming = false;
  root.voiceWaveCropSnapshot = null;
  const title = $('[data-title]');
  const date = $('[data-date]');
  if (title && !title.value) title.value = 'A voice worth keeping';
  if (date && !date.value) date.value = 'Theme editor preview';
  root.voiceWaveCanonicalSnapshot = {
    memoryId: model.memoryId,
    canonicalMinMax: demoPairs,
    durationMs: model.durationMs,
    sequenceHash: 'theme-editor-demo-v1'
  };
  window.VM_SELECTED_VARIANT = demoVariant;
  applyVariantSelection(demoVariant);
  unlockDesign();
  unlockProduct();
  unlockReview();
  root.dispatchEvent(new CustomEvent('vm:canonical-ready', { detail: root.voiceWaveCanonicalSnapshot }));
  root.dispatchEvent(new CustomEvent('vm:v4-render-request'));
  const requestedStep = Math.max(1, Math.min(4, Number(root.dataset.editorPreviewStep || 1)));
  setStep(requestedStep);
  syncDesignPreview();
  root.dispatchEvent(new CustomEvent('vm:v4-render-request'));
}

if (isEditorPreview) window.setTimeout(installEditorPreview, 50);

let mockupLoadingTimer = null;
let mockupLoadingStartTime = 0;
let mockupAnimFrame = null;
let mockupCanvasTime = 0;
let proofLoadingAnimFrame = null;
let proofLoadingStartTime = 0;

const MOCKUP_STAGES = [
  { atSec: 0, headline: 'Placing your soundwave onto product…', sub: 'Mapping exact dimensions, color palettes, and orientation.' },
  { atSec: 7, headline: 'Applying high-definition print texture…', sub: 'Rendering realistic surface lighting, depth, and materials.' },
  { atSec: 16, headline: 'Finalizing studio-quality mockup…', sub: 'Synthesizing front, perspective, and detail views.' },
  { atSec: 28, headline: 'Almost done! Wrapping up preview…', sub: 'Final pixel rendering in progress. Production Proof is already ready above.' }
];

function drawSilkRibbonWave(ctx, width, height, t) {
  ctx.clearRect(0, 0, width, height);
  const centerY = height / 2;
  const numLines = 9;

  for (let i = 0; i < numLines; i++) {
    const offset = (i - numLines / 2) * 3.5;
    const phase = i * 0.22;
    const alpha = 0.85 - (Math.abs(i - numLines / 2) / (numLines / 2)) * 0.6;

    ctx.beginPath();
    ctx.strokeStyle = `rgba(184, 138, 68, ${alpha})`;
    ctx.lineWidth = i === Math.floor(numLines / 2) ? 2 : 1;

    for (let x = 0; x <= width; x += 2) {
      const distFromCenter = Math.abs(x - width / 2) / (width / 2);
      const envelope = Math.pow(Math.cos(distFromCenter * Math.PI / 2), 2);
      const y = centerY + offset + Math.sin(x * 0.014 + t * 0.035 + phase) * 26 * envelope;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}

function startMockupLoadingAnimation() {
  if (mockupLoadingTimer) return;
  mockupLoadingStartTime = Date.now();

  const headlineEl = $('[data-loading-headline]');
  const subEl = $('[data-loading-sub]');
  const barEl = $('[data-loading-bar]');
  const canvas = $('[data-product-loading-canvas]');

  let ctx = null;
  let canvasWidth = 0;
  let canvasHeight = 0;

  if (canvas) {
    const rect = canvas.getBoundingClientRect();
    const parentRect = canvas.parentElement?.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvasWidth = Math.max(300, rect.width || parentRect?.width || 360);
    canvasHeight = Math.max(120, rect.height || parentRect?.height || 140);
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
  }

  const animLoop = () => {
    mockupCanvasTime += 1;
    if (ctx && canvasWidth && canvasHeight) {
      drawSilkRibbonWave(ctx, canvasWidth, canvasHeight, mockupCanvasTime);
    }
    mockupAnimFrame = requestAnimationFrame(animLoop);
  };
  mockupAnimFrame = requestAnimationFrame(animLoop);

  const update = () => {
    const elapsedSec = (Date.now() - mockupLoadingStartTime) / 1000;
    const progress = Math.min(95, Math.floor(5 + (90 * (1 - Math.exp(-elapsedSec / 14)))));
    if (barEl) barEl.style.width = `${progress}%`;

    let currentStage = MOCKUP_STAGES[0];
    for (let i = MOCKUP_STAGES.length - 1; i >= 0; i--) {
      if (elapsedSec >= MOCKUP_STAGES[i].atSec) {
        currentStage = MOCKUP_STAGES[i];
        break;
      }
    }
    if (headlineEl && headlineEl.textContent !== currentStage.headline) {
      headlineEl.textContent = currentStage.headline;
    }
    if (subEl && subEl.textContent !== currentStage.sub) {
      subEl.textContent = currentStage.sub;
    }
  };

  update();
  mockupLoadingTimer = setInterval(update, 300);
}

function stopMockupLoadingAnimation() {
  if (mockupLoadingTimer) {
    clearInterval(mockupLoadingTimer);
    mockupLoadingTimer = null;
  }
  if (mockupAnimFrame) {
    cancelAnimationFrame(mockupAnimFrame);
    mockupAnimFrame = null;
  }
  const barEl = $('[data-loading-bar]');
  if (barEl) barEl.style.width = '100%';
}

function startProofLoadingAnimation() {
  if (proofLoadingAnimFrame) return;
  proofLoadingStartTime = Date.now();
  const canvas = $('[data-proof-loading-canvas]');
  const barEl = $('[data-proof-loading-bar]');
  let ctx = null;
  let width = 0;
  let height = 0;

  if (canvas) {
    const rect = canvas.getBoundingClientRect();
    const parentRect = canvas.parentElement?.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    width = Math.max(300, rect.width || parentRect?.width || 360);
    height = Math.max(120, rect.height || parentRect?.height || 140);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
  }

  const animate = () => {
    if (ctx && width && height) drawSilkRibbonWave(ctx, width, height, (Date.now() - proofLoadingStartTime) / 16);
    if (barEl) {
      const elapsedSec = (Date.now() - proofLoadingStartTime) / 1000;
      barEl.style.width = `${Math.min(95, Math.floor(5 + 90 * (1 - Math.exp(-elapsedSec / 14))))}%`;
    }
    proofLoadingAnimFrame = requestAnimationFrame(animate);
  };
  proofLoadingAnimFrame = requestAnimationFrame(animate);
}

function stopProofLoadingAnimation() {
  if (proofLoadingAnimFrame) cancelAnimationFrame(proofLoadingAnimFrame);
  proofLoadingAnimFrame = null;
  const barEl = $('[data-proof-loading-bar]');
  if (barEl) barEl.style.width = '100%';
}
